import React, { useState, useEffect, useMemo, useCallback, useRef } from 'react';
import { useParams, useNavigate, useLocation } from 'react-router-dom';
import { BRAND } from '../../../config/brand';
import {
  Box,
  Container,
  Typography,
  CircularProgress,
  Alert,
  Grid,
  Card,
  CardContent,
  CardActionArea,
  Paper,
  Button,
  Icon,
  Divider,
  Chip,
  Tooltip,
  ToggleButtonGroup,
  ToggleButton,
  AppBar,
  Toolbar,
  IconButton,
  Menu,
  MenuItem,
  Drawer,
  List,
  ListItem,
  ListItemButton,
  ListItemText,
  useMediaQuery,
  Snackbar,
  Switch,
  FormControlLabel,
} from '@mui/material';
// Avatar artık kullanılmıyor; eski profil butonu yerine "Menü" Button geldi.
import { useTheme } from '@mui/material/styles';
import ArrowBackIcon from '@mui/icons-material/ArrowBack';
import ChairIcon from '@mui/icons-material/Chair';
import NotesIcon from '@mui/icons-material/Notes';
import AllInclusiveIcon from '@mui/icons-material/AllInclusive';
import TableRestaurantIcon from '@mui/icons-material/TableRestaurant';
import LogoutIcon from '@mui/icons-material/Logout';
import PersonIcon from '@mui/icons-material/Person';
import RestaurantMenuIcon from '@mui/icons-material/RestaurantMenu';
import FullscreenIcon from '@mui/icons-material/Fullscreen';
import FullscreenExitIcon from '@mui/icons-material/FullscreenExit';
import LockIcon from '@mui/icons-material/Lock';
import MoreVertIcon from '@mui/icons-material/MoreVert';
import MenuIcon from '@mui/icons-material/Menu';
import FilterListIcon from '@mui/icons-material/FilterList';
import SettingsIcon from '@mui/icons-material/Settings';
import SaveIcon from '@mui/icons-material/Save';
import CloseIcon from '@mui/icons-material/Close';
import RestartAltIcon from '@mui/icons-material/RestartAlt';
import BackspaceIcon from '@mui/icons-material/Backspace';
import GridViewIcon from '@mui/icons-material/GridView';
import PrintIcon from '@mui/icons-material/Print';
import TextFieldsIcon from '@mui/icons-material/TextFields';
import GridLayout, { WidthProvider } from 'react-grid-layout';
import 'react-grid-layout/css/styles.css';
import 'react-resizable/css/styles.css';
import { useAuth } from '../../../contexts/AuthContext';
import RestaurantLockScreenModal, { RestaurantLoginInfo } from './RestaurantLockScreenModal';
import EndOfDayPrintModal from '../../reports/components/EndOfDayPrintModal';
import { APP_VERSION } from '../../../version';
import ownAuthSession from '../../../services/auth/ownAuthSession';
import { getPostLogoutRoute } from '../../../services/auth/terminalSession';
import firebaseDataService from '../../../services/FirebaseDataService';
import { RestaurantTableData, RestaurantTableGroupData } from './restaurantTableTypes';
import { 
  restaurantScreenApi, 
  restaurantTableApi, 
  restaurantTableGroupApi,
  restaurantActiveSaleApi,
  RestaurantScreenDto,
  RestaurantTableDto,
  RestaurantTableGroupDto,
  RestaurantActiveSaleDto,
} from '../../../services/restaurantApiService';
import { getCustomerDisplaySettings } from '../../../services/salesService';
import salesService from '../../../services/salesService';
import { getSellerByScreen, type SellerSnapshot } from '../../../services/sellerInfoService';
import AutoLockCountdownModal from '../quickSale/components/modals/AutoLockCountdownModal';
import { useAutoLock } from '../../../hooks/useAutoLock';
import LocalPrinterSettingsModal from './components/LocalPrinterSettingsModal';
import PavoOrderTrackingModal from './components/PavoOrderTrackingModal';
import PavoPaymentClosedInfoModal from './components/PavoPaymentClosedInfoModal';
import TableNameFontSizeModal, { DEFAULT_TABLE_NAME_FONT_SCALE } from './TableNameFontSizeModal';
import {
  getUseTableOpenerForReceipt,
  setUseTableOpenerForReceipt,
  pruneStaleOpeners,
} from './utils/tableOpenersStorage';
import { pavoApiService, PavoPaymentRequest } from '../../../services/pavoApiService';
import { pavoSignalRService } from '../../../services/pavoSignalRService';
import PointOfSaleIcon from '@mui/icons-material/PointOfSale';
import { useTranslation } from 'react-i18next';
import GuideButton from '../../common/guide/GuideButton';

interface RestaurantSetup {
  id: number;
  name: string;
  branchId?: string | null;
  quickSaleScreenId?: number; // ✅ YENİ: İlişkili QuickSaleScreen ID
  useKitchenScreen?: boolean; // ✅ YENİ: Mutfak ekranı ayarı
}

const AutoWidthGridLayout = WidthProvider(GridLayout);

const RestaurantScreen: React.FC = () => {
  const { t } = useTranslation('salesSystem');
  const { setupId: setupIdParam } = useParams<{ setupId: string }>();
  const setupId = setupIdParam ? parseInt(setupIdParam, 10) : null;
  const navigate = useNavigate();
  const location = useLocation();
  const { currentUser: authUser, logout, userDetails } = useAuth();
  
  // ✅ DEBUG: Component mount/unmount logları
  useEffect(() => {
    console.log('🟢 [RestaurantScreen] ========== COMPONENT MOUNTED ==========');
    console.log('🟢 [RestaurantScreen] authUser:', authUser ? 'VAR' : 'YOK', 'uid:', authUser?.uid);
    console.log('🟢 [RestaurantScreen] setupId:', setupId);
    
    return () => {
      console.log('🔴 [RestaurantScreen] ========== COMPONENT UNMOUNTING ==========');
      console.log('🔴 [RestaurantScreen] Unmount anında authUser:', authUser ? 'VAR' : 'YOK');
      // Stack trace al
      console.log('🔴 [RestaurantScreen] Stack trace:', new Error().stack);
    };
  }, []);
  
  const [companyId, setCompanyId] = useState<string | null>(null);
  const [setupDetails, setSetupDetails] = useState<RestaurantSetup | null>(null);
  // ✅ PR2 (Invoice Seller Snapshot Refactor): Backend tarafından çözülen satıcı snapshot
  // Companies vs Branches.Firm* karar mantığı backend'de; burada sadece UI/log için tutulur.
  const [seller, setSeller] = useState<SellerSnapshot | null>(null);
  const [userDisplayName, setUserDisplayName] = useState<string>('');
  const [anchorElUser, setAnchorElUser] = useState<null | HTMLElement>(null);
  
  // Admin kontrolü - sadece admin geri butonunu görsün
  const isAdmin = userDetails?.role === 'admin';
  
  // Tam ekran state'i
  const [isFullscreen, setIsFullscreen] = useState(false);
  
  // Kilit ekranı ve kullanıcı bilgileri
  // ✅ Session boyunca kullanıcı bilgisini sakla (sayfa yenileme = kilit)
  const [isLocked, setIsLocked] = useState(() => {
    if (!setupIdParam) return true;
    const userKey = `restaurant_user_${setupIdParam}`;
    const savedUser = sessionStorage.getItem(userKey);
    return !savedUser; // Kullanıcı varsa kilitli değil
  });
  
  // ✅ YENİ: Garson veya Kasiyer bilgisi (RestaurantLoginInfo tipi)
  const [restaurantLoginUser, setRestaurantLoginUser] = useState<RestaurantLoginInfo | null>(() => {
    // Session'dan kullanıcı bilgisini yükle (QuickSale'den geri dönüşte)
    if (!setupIdParam) return null;
    const userKey = `restaurant_user_${setupIdParam}`;
    const savedUser = sessionStorage.getItem(userKey);
    return savedUser ? JSON.parse(savedUser) : null;
  });
  
  // ✅ Geriye uyumluluk için alias (eski kod için)
  const currentWaiter = restaurantLoginUser;
  
  const [allTables, setAllTables] = useState<RestaurantTableData[]>([]);
  const [tableGroups, setTableGroups] = useState<RestaurantTableGroupData[]>([]);
  const [selectedGroupIdFilter, setSelectedGroupIdFilter] = useState<string>(() => {
    if (!setupIdParam) return 'all';
    const saved = sessionStorage.getItem(`restaurant_group_${setupIdParam}`);
    return saved || 'all';
  });
  
  // ✅ YENİ: SQL'den aktif satışları tut
  const [activeSales, setActiveSales] = useState<{ [tableId: string]: any }>({});
  
  // Pavo aktif ödemeleri (masa adı -> ödeme bilgisi)
  const [activePavoPayments, setActivePavoPayments] = useState<PavoPaymentRequest[]>([]);
  const [pavoTrackingOpen, setPavoTrackingOpen] = useState(false);

  // ✨ Pavo Restoran Ödendi Onay — kasiyer KOYU MOR masaya tıklayınca açılan info modal
  const [pavoClosedInfoModal, setPavoClosedInfoModal] = useState<{
    open: boolean;
    sale: RestaurantActiveSaleDto | null;
    tableName?: string;
  }>({ open: false, sale: null });
  
  // Oto kilitleme ayarları
  const [autoLockEnabled, setAutoLockEnabled] = useState(false);
  const [autoLockTimeoutSeconds, setAutoLockTimeoutSeconds] = useState(30);

  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  // Layout düzenleme state'leri
  const [isLayoutPinDialogOpen, setIsLayoutPinDialogOpen] = useState(false);
  const [endOfDayPrintModalOpen, setEndOfDayPrintModalOpen] = useState(false);
  const [layoutPinInput, setLayoutPinInput] = useState('');
  const [layoutPinError, setLayoutPinError] = useState(false);
  const [isLayoutEditMode, setIsLayoutEditMode] = useState(false);
  const [savedLayouts, setSavedLayouts] = useState<Record<string, GridLayout.Layout[]>>({});
  const [editingLayout, setEditingLayout] = useState<GridLayout.Layout[]>([]);
  const [editingGroupId, setEditingGroupId] = useState<string>('all');
  const [layoutSaving, setLayoutSaving] = useState(false);
  const [isLocalPrinterModalOpen, setIsLocalPrinterModalOpen] = useState(false);

  // Masa ismi yazı boyutu (scale multiplier; 1.0 = varsayılan orta)
  const [tableNameFontScale, setTableNameFontScale] = useState<number>(DEFAULT_TABLE_NAME_FONT_SCALE);
  const [isFontSizeModalOpen, setIsFontSizeModalOpen] = useState(false);
  // Edit moduna girmeden önceki font scale (İptal edilirse geri yüklenir)
  const originalFontScaleRef = useRef<number>(DEFAULT_TABLE_NAME_FONT_SCALE);

  // ✅ YENİ: Masa kartlarında "kaç dakikadır açık" sayacı (sadece rakam, ikon yok)
  // Ayar setup-bazlı localStorage'da tutulur, DB'ye yazılmaz.
  // Toggle, layout düzenleme modunun üst toolbar'ında bulunur.
  const showTableOpenDurationKey = `restaurant_show_table_open_duration_${setupIdParam || 'default'}`;
  const [showTableOpenDuration, setShowTableOpenDuration] = useState<boolean>(() => {
    try {
      return localStorage.getItem(showTableOpenDurationKey) === 'true';
    } catch {
      return false;
    }
  });
  useEffect(() => {
    try {
      localStorage.setItem(showTableOpenDurationKey, showTableOpenDuration ? 'true' : 'false');
    } catch { /* localStorage erişimi engellendiyse sessizce geç */ }
  }, [showTableOpenDuration, showTableOpenDurationKey]);

  // Dakika sayacı için 30 saniyede bir tick — sadece ayar açıkken çalışır
  const [openDurationTick, setOpenDurationTick] = useState<number>(() => Date.now());
  useEffect(() => {
    if (!showTableOpenDuration) return;
    setOpenDurationTick(Date.now());
    const id = setInterval(() => setOpenDurationTick(Date.now()), 30000);
    return () => clearInterval(id);
  }, [showTableOpenDuration]);

  // ✅ YENİ: Adisyon fişinde "masayı ilk açan garson" gösterilsin mi? (lokal — DB ile alakası yok)
  // Toggle açıkken adisyon fişine, masaya ilk PIN ile giren garsonun adı yazılır.
  // Mapping ve toggle helper'ları utils/tableOpenersStorage.ts içinde.
  const [useTableOpenerForReceipt, setUseTableOpenerForReceiptState] = useState<boolean>(() =>
    getUseTableOpenerForReceipt(setupIdParam)
  );
  useEffect(() => {
    setUseTableOpenerForReceipt(setupIdParam, useTableOpenerForReceipt);
  }, [setupIdParam, useTableOpenerForReceipt]);

  // Snackbar helper
  const [snackbarOpen, setSnackbarOpen] = useState(false);
  const [snackbarMessage, setSnackbarMessage] = useState('');
  
  const showSnackbar = (message: string, severity: 'success' | 'error' | 'warning' = 'success') => {
    setSnackbarMessage(message);
    setSnackbarOpen(true);
  };
  
  const handleSnackbarClose = () => {
    setSnackbarOpen(false);
  };

  // Add localTableCarts state and local add/remove functions
  const [localTableCarts, setLocalTableCarts] = useState<{ [tableId: string]: any[] }>(() => {
    try {
      const savedCarts = localStorage.getItem('restaurantTableCarts');
      return savedCarts ? JSON.parse(savedCarts) : {};
    } catch (error) {
      console.error("Failed to load localTableCarts from localStorage:", error);
      return {};
    }
  });

  // Persist localTableCarts to localStorage whenever it changes
  useEffect(() => {
    localStorage.setItem('restaurantTableCarts', JSON.stringify(localTableCarts));
  }, [localTableCarts]);

  // Listen for changes in localStorage from other tabs/windows (e.g., TableOrderScreen)
  useEffect(() => {
    const handleStorageChange = (event: StorageEvent) => {
      if (event.key === 'restaurantTableCarts' && event.newValue) {
        try {
          setLocalTableCarts(JSON.parse(event.newValue));
        } catch (error) {
          console.error("Failed to parse localTableCarts from storage event:", error);
        }
      }
    };

    window.addEventListener('storage', handleStorageChange);
    return () => {
      window.removeEventListener('storage', handleStorageChange);
    };
  }, []);

  const addToCart = (tableId: string, item: any) => {
    setLocalTableCarts(prev => ({
      ...prev,
      [tableId]: [...(prev[tableId] || []), item],
    }));
  };

  const removeFromCart = (tableId: string, itemIndex: number) => {
    setLocalTableCarts(prev => {
      const updatedCart = [...(prev[tableId] || [])];
      updatedCart.splice(itemIndex, 1);
      return {
        ...prev,
        [tableId]: updatedCart,
      };
    });
  };

  const LAYOUT_COLS = 40;
  const LAYOUT_ROW_HEIGHT = 30;

  const DEFAULT_CARD_W = 4;
  const DEFAULT_CARD_H = 4;
  const MIN_CARD_W = 2;
  const MIN_CARD_H = 2;
  const CARDS_PER_ROW = Math.floor(LAYOUT_COLS / DEFAULT_CARD_W);

  const generateDefaultLayout = useCallback((tables: RestaurantTableData[]): GridLayout.Layout[] => {
    return tables.map((table, index) => ({
      i: table.id.toString(),
      x: (index % CARDS_PER_ROW) * DEFAULT_CARD_W,
      y: Math.floor(index / CARDS_PER_ROW) * DEFAULT_CARD_H,
      w: DEFAULT_CARD_W,
      h: DEFAULT_CARD_H,
      minW: MIN_CARD_W,
      minH: MIN_CARD_H,
    }));
  }, []);

  // PIN numpad handler'ları
  const handlePinDigit = (digit: string) => {
    if (layoutPinInput.length < 5) {
      const newPin = layoutPinInput + digit;
      setLayoutPinInput(newPin);
      setLayoutPinError(false);
      if (newPin.length === 5) {
        if (newPin === '13579') {
          setIsLayoutPinDialogOpen(false);
          setLayoutPinInput('');
          enterEditMode();
        } else {
          setLayoutPinError(true);
          setTimeout(() => { setLayoutPinInput(''); setLayoutPinError(false); }, 800);
        }
      }
    }
  };

  const handlePinBackspace = () => {
    setLayoutPinInput(prev => prev.slice(0, -1));
    setLayoutPinError(false);
  };

  const enterEditMode = () => {
    const groupId = selectedGroupIdFilter;
    const groupTables = getTablesForGroup(groupId);
    const groupLayout = savedLayouts[groupId];
    const currentLayout = groupLayout && groupLayout.length > 0
      ? groupLayout.map(item => ({ ...item, minW: MIN_CARD_W, minH: MIN_CARD_H }))
      : generateDefaultLayout(groupTables);
    setEditingGroupId(groupId);
    setEditingLayout(currentLayout);
    // Edit moduna girerken mevcut font scale'i snapshot al (İptal edilirse geri yüklenecek)
    originalFontScaleRef.current = tableNameFontScale;
    setIsLayoutEditMode(true);
  };

  const exitEditMode = () => {
    setIsLayoutEditMode(false);
    setEditingLayout([]);
    setEditingGroupId('all');
    // İptal: font scale'i edit moduna girerkenki haline geri yükle
    setTableNameFontScale(originalFontScaleRef.current);
  };

  const handleLayoutChange = (newLayout: GridLayout.Layout[]) => {
    setEditingLayout(newLayout);
  };

  const handleSaveLayout = async () => {
    if (!setupId) return;
    setLayoutSaving(true);
    try {
      const newLayouts = { ...savedLayouts, [editingGroupId]: editingLayout };
      const layoutWithVersion = {
        __gridVersion: 2,
        __tableNameFontScale: tableNameFontScale,
        ...newLayouts,
      };
      const layoutJson = JSON.stringify(layoutWithVersion);
      await restaurantScreenApi.saveLayout(setupId, layoutJson);
      setSavedLayouts(newLayouts);
      // Kaydedilen scale artık "orijinal" oldu
      originalFontScaleRef.current = tableNameFontScale;
      setIsLayoutEditMode(false);
      const groupName = editingGroupId === 'all' ? t('restaurantScreen.all') : (tableGroups.find(g => g.id === editingGroupId)?.name || editingGroupId);
      showSnackbar(`"${groupName}" grubu düzeni kaydedildi`);
    } catch (err) {
      console.error('Layout kaydetme hatası:', err);
      showSnackbar(t('restaurantScreen.errors.layoutSaveFailed'));
    } finally {
      setLayoutSaving(false);
    }
  };

  const handleResetLayout = async () => {
    if (!setupId) return;
    setLayoutSaving(true);
    try {
      const newLayouts = { ...savedLayouts };
      delete newLayouts[editingGroupId];
      const hasAny = Object.keys(newLayouts).length > 0;
      // Tüm layout temizlenince font scale de varsayılana döner
      const nextFontScale = hasAny ? tableNameFontScale : DEFAULT_TABLE_NAME_FONT_SCALE;
      await restaurantScreenApi.saveLayout(
        setupId,
        hasAny
          ? JSON.stringify({ __gridVersion: 2, __tableNameFontScale: nextFontScale, ...newLayouts })
          : null
      );
      setSavedLayouts(newLayouts);
      setTableNameFontScale(nextFontScale);
      originalFontScaleRef.current = nextFontScale;
      setIsLayoutEditMode(false);
      const groupName = editingGroupId === 'all' ? t('restaurantScreen.all') : (tableGroups.find(g => g.id === editingGroupId)?.name || editingGroupId);
      showSnackbar(`"${groupName}" grubu düzeni sıfırlandı`);
    } catch (err) {
      console.error('Layout sıfırlama hatası:', err);
      showSnackbar(t('restaurantScreen.errors.layoutResetFailed'));
    } finally {
      setLayoutSaving(false);
    }
  };

  useEffect(() => {
    if (authUser) {
      firebaseDataService.getUserData(authUser.uid).then((userData) => {
        if (userData) {
          setCompanyId(userData.companyId);
          
          // Kullanıcı adını set et
          const firstName = userData.firstName || '';
          const lastName = userData.lastName || '';
          const displayName = `${firstName} ${lastName}`.trim() || authUser.email || 'Kullanıcı';
          setUserDisplayName(displayName);
        } else {
          setError("Kullanıcıya ait şirket ID'si bulunamadı.");
          setLoading(false);
        }
      }).catch((err) => {
        console.error("Şirket ID'si alınırken hata:", err);
        setError("Şirket ID'si alınamadı.");
        setLoading(false);
      });
    } else {
        setError("Giriş yapmış kullanıcı bulunamadı.");
        setLoading(false);
    }
  }, [authUser]);

  // ✅ YENİ: Aktif satışları SQL'den çek
  // 🚀 BANT GENİŞLİĞİ: Tam liste yerine HAFİF özet endpoint kullanılır.
  // Items ve WaiterActionsJson gelmez — masa kartlarının ihtiyacı olan tüm
  // alanlar (renk durumu, Pavo bilgileri, OpenedAt, tutarlar) özet DTO'da mevcut.
  const loadActiveSales = useCallback(async () => {
    if (!setupId || isNaN(setupId)) return;
    
    try {
      const { restaurantActiveSaleApi } = await import('../../../services/restaurantApiService');
      const activeSalesData = await restaurantActiveSaleApi.getScreenSummary(setupId);
      
      // TableId'ye göre map oluştur
      const salesByTableId: { [tableId: string]: any } = {};
      activeSalesData.forEach(sale => {
        salesByTableId[sale.TableId.toString()] = sale;
      });
      
      setActiveSales(salesByTableId);
      console.log('✅ [RestaurantScreen] Aktif satışlar yüklendi (özet):', activeSalesData.length, 'adet');

      // ✅ Adisyon-fişi-açan-garson kayıtlarından artık aktif olmayan masaları temizle
      try {
        pruneStaleOpeners(setupIdParam, activeSalesData.map(s => s.TableId));
      } catch (err) {
        console.warn('[RestaurantScreen] pruneStaleOpeners hatası:', err);
      }

      // 🐛 MasaSüresi DEBUG: ilk aktif satışın OpenedAt formatını logla
      if (activeSalesData.length > 0) {
        const sample = activeSalesData[0];
        console.log('🕐 [MasaSüresi RAW SAMPLE - API yanıtı]', {
          tableId: sample.TableId,
          tableName: sample.TableName,
          rawOpenedAt: sample.OpenedAt,
          rawOpenedAtType: typeof sample.OpenedAt,
          hasTimezoneMarker: /Z$|[+-]\d{2}:?\d{2}$/.test((sample.OpenedAt || '').trim()),
          // ham parse (browser local time olarak yorumlar — naive ise yanlış olur)
          naiveParsedISO: sample.OpenedAt ? new Date(sample.OpenedAt).toISOString() : null,
          naiveDiffMin: sample.OpenedAt ? Math.floor((Date.now() - new Date(sample.OpenedAt).getTime()) / 60000) : null,
          // "Z" eklenmiş parse (UTC olarak yorumla)
          forcedUtcParsedISO: sample.OpenedAt ? new Date(sample.OpenedAt + (/Z$|[+-]\d{2}:?\d{2}$/.test(sample.OpenedAt) ? '' : 'Z')).toISOString() : null,
          forcedUtcDiffMin: sample.OpenedAt ? Math.floor((Date.now() - new Date(sample.OpenedAt + (/Z$|[+-]\d{2}:?\d{2}$/.test(sample.OpenedAt) ? '' : 'Z')).getTime()) / 60000) : null,
          nowISO: new Date().toISOString(),
          browserTimezone: Intl.DateTimeFormat().resolvedOptions().timeZone,
          browserTzOffsetMin: new Date().getTimezoneOffset(),
        });
      }
    } catch (err) {
      console.error("❌ [RestaurantScreen] Aktif satışlar yüklenirken hata:", err);
    }
  }, [setupId]);

  useEffect(() => {
    if (!setupId || isNaN(setupId)) {
        setError("Kurulum ID'si bulunamadı veya geçersiz.");
        setLoading(false);
        return;
    }
    if (!companyId) {
        return;
    }

    setLoading(true);
    setError(null);

    // API'den restoran ekranı, masa grupları ve masaları çek
    Promise.all([
      restaurantScreenApi.getById(setupId),
      restaurantTableGroupApi.getByRestaurantScreenId(setupId),
      restaurantTableApi.getByRestaurantScreenId(setupId)
    ])
      .then(([screenData, groupsData, tablesData]) => {
        // Restoran ekranı
        const restaurantData = {
          id: screenData.Id,
          name: screenData.Name,
          branchId: screenData.BranchId,
          quickSaleScreenId: screenData.QuickSaleScreenId,
          useKitchenScreen: screenData.UseKitchenScreen ?? false
        };
        
        // Layout JSON parse (grup bazlı veya eski format geriye uyumluluk)
        if (screenData.TableLayoutJson) {
          try {
            const parsed = JSON.parse(screenData.TableLayoutJson);

            // __gridVersion: 2 varsa yeni 40-kolon format, yoksa eski 20-kolon
            const isNewFormat = parsed && typeof parsed === 'object' && !Array.isArray(parsed) && parsed.__gridVersion === 2;

            const migrateLayoutItems = (items: GridLayout.Layout[]): GridLayout.Layout[] => {
              return items.map(item => ({
                ...item,
                x: item.x * 2,
                y: item.y * 2,
                w: item.w * 2,
                h: item.h * 2,
              }));
            };

            if (isNewFormat) {
              // Yeni format: meta key'leri çıkarıp grup layout'larını kullan
              const { __gridVersion, __tableNameFontScale, ...groupLayouts } = parsed;
              setSavedLayouts(groupLayouts as Record<string, GridLayout.Layout[]>);
              // Masa ismi font scale (varsa yükle)
              if (typeof __tableNameFontScale === 'number' && __tableNameFontScale > 0) {
                setTableNameFontScale(__tableNameFontScale);
                originalFontScaleRef.current = __tableNameFontScale;
              }
            } else if (Array.isArray(parsed)) {
              // Eski format: düz array -> migrate et
              console.log('📐 [LAYOUT] Eski 20-kolon layout (array) tespit edildi, migrate ediliyor...');
              setSavedLayouts({ all: migrateLayoutItems(parsed) });
            } else if (parsed && typeof parsed === 'object') {
              // Eski format: grup objesi -> migrate et
              console.log('📐 [LAYOUT] Eski 20-kolon layout (grup) tespit edildi, migrate ediliyor...');
              const migrated: Record<string, GridLayout.Layout[]> = {};
              for (const [groupId, layoutArr] of Object.entries(parsed)) {
                migrated[groupId] = Array.isArray(layoutArr) ? migrateLayoutItems(layoutArr as GridLayout.Layout[]) : layoutArr as GridLayout.Layout[];
              }
              setSavedLayouts(migrated);
            }
          } catch (e) {
            console.warn('TableLayoutJson parse hatası:', e);
          }
        }
        
        setSetupDetails(restaurantData);

        // Oto kilitleme ayarlarını ilişkili QuickSaleScreen'den yükle
        if (restaurantData.quickSaleScreenId) {
          console.log('🔒 [RESTORAN] QuickSaleScreen yükleniyor, ID:', restaurantData.quickSaleScreenId);
          salesService.getQuickSaleScreenById(restaurantData.quickSaleScreenId)
            .then((qsScreen: any) => {
              const lockEnabled = qsScreen.AutoLockEnabled || qsScreen.autoLockEnabled || false;
              const lockTimeout = qsScreen.AutoLockTimeoutSeconds || qsScreen.autoLockTimeoutSeconds || 30;
              console.log('🔒 [RESTORAN] Oto kilitleme ayarları:', { lockEnabled, lockTimeout });
              setAutoLockEnabled(lockEnabled);
              setAutoLockTimeoutSeconds(lockTimeout);
            })
            .catch((err: any) => {
              console.warn('⚠️ [RESTORAN] Oto kilitleme ayarları yüklenemedi:', err);
            });
        } else {
          console.log('🔒 [RESTORAN] quickSaleScreenId yok, oto kilitleme ayarları yüklenemeyecek');
        }

        // Masa grupları - doğal sıralama ile
        const groups: RestaurantTableGroupData[] = groupsData.map((g: RestaurantTableGroupDto) => ({
          id: g.Id.toString(),
          name: g.Name
        })).sort((a, b) => a.name.localeCompare(b.name, undefined, { numeric: true, sensitivity: 'base' }));
        setTableGroups(groups);

        // Masalar - doğal sıralama ile
        const tables: RestaurantTableData[] = tablesData.map((t: RestaurantTableDto) => ({
          id: t.Id.toString(),
          name: t.Name,
          capacity: t.Capacity,
          groupId: t.TableGroupId?.toString() || undefined
        })).sort((a, b) => a.name.localeCompare(b.name, undefined, { numeric: true, sensitivity: 'base' }));
        setAllTables(tables);

        // ✅ YENİ: Aktif satışları da yükle
        loadActiveSales();

        setLoading(false);
      })
      .catch((err) => {
        console.error("Restoran ekranı verileri yüklenirken hata:", err);
        setError("Veriler yüklenirken bir hata oluştu: " + (err.response?.data?.Message || err.message));
        setLoading(false);
      });
  }, [companyId, setupId, loadActiveSales]);
  
  // 🚀 YENİ MİMARİ: Gerçek zamanlı güncelleme SignalR ile ("değişince bildir").
  // Polling artık yalnızca seyrek bir GÜVENLİK AĞI (SignalR koparsa/event kaçarsa).
  // Eski: her 10 sn'de tam liste (Items + WaiterActionsJson) → devasa trafik.
  // Yeni: 60 sn'de bir hafif özet + SignalR event'inde anında tazeleme.
  useEffect(() => {
    if (!setupId || isNaN(setupId) || loading) return;
    
    const interval = setInterval(() => {
      loadActiveSales();
    }, 60000); // 60 saniye (fallback)
    
    return () => clearInterval(interval);
  }, [setupId, loading, loadActiveSales]);

  // 🚀 SignalR: Restoran API hub'ına bağlan — masa durumu değişince anında tazele.
  // Backend masa açma/kapama/taşıma, adisyon yazdırma, split/merge ve Pavo onayı
  // sonrası "OnActiveSalesChanged" yayınlar.
  useEffect(() => {
    if (!setupId || isNaN(setupId) || loading) return;
    const callbackId = `restaurant_screen_${setupId}`;
    let cancelled = false;

    (async () => {
      try {
        const { restaurantSignalRService } = await import('../../../services/restaurantSignalRService');
        if (cancelled) return;
        restaurantSignalRService.onActiveSalesChanged(callbackId, () => {
          loadActiveSales();
        });
        await restaurantSignalRService.connect(setupId);
      } catch (err) {
        console.warn('[RestaurantScreen] Restoran SignalR bağlantısı kurulamadı (fallback polling devrede):', err);
      }
    })();

    return () => {
      cancelled = true;
      import('../../../services/restaurantSignalRService').then(({ restaurantSignalRService }) => {
        restaurantSignalRService.removeActiveSalesChangedCallback(callbackId);
      }).catch(() => {});
    };
  }, [setupId, loading, loadActiveSales]);

  // Pavo aktif ödemelerini yükle
  const loadActivePavoPayments = useCallback(async () => {
    if (!companyId) return;
    try {
      const payments = await pavoApiService.getActivePayments(companyId, setupDetails?.branchId || undefined);
      setActivePavoPayments(payments);
    } catch (err) {
      console.warn('[RestaurantScreen] Pavo aktif ödemeler yüklenemedi:', err);
    }
  }, [companyId, setupDetails?.branchId]);

  useEffect(() => {
    if (!companyId || loading) return;
    loadActivePavoPayments();
    // 🚀 BANT GENİŞLİĞİ: 10 sn → 60 sn. Pavo ödeme durumu değişiklikleri zaten
    // SignalR event'leriyle (OnPaymentCompleted/Failed/Cancelled) anında yansıyor;
    // bu polling yalnızca güvenlik ağı. Ayrıca backend artık slim yanıt döner
    // (ItemsJson/PaymentResultJson yok) + eski takılı kayıtlar otomatik expire edilir.
    const interval = setInterval(loadActivePavoPayments, 60000);
    return () => clearInterval(interval);
  }, [companyId, loading, loadActivePavoPayments]);

  // 🚀 SignalR: Pavo hub'ına bağlan — RestaurantScreen'de kayıtlı Pavo event
  // callback'leri (OnPaymentCompleted, OnTablePavoClosed vb.) ancak bağlantı
  // kuruluysa tetiklenir. Önceden bağlantı yalnız QuickSaleScreen'de kuruluyordu;
  // masalar ekranı açıkken event kaçabiliyordu.
  useEffect(() => {
    if (!companyId || loading) return;
    pavoSignalRService.connect(companyId, setupDetails?.branchId || undefined).catch((err) => {
      console.warn('[RestaurantScreen] Pavo SignalR bağlantısı kurulamadı:', err);
    });
  }, [companyId, loading, setupDetails?.branchId]);

  // 🚀 SignalR: Pavo cihazı restoran modunda masa değiştirdiğinde/kapattığında
  // (RestaurantDeviceController broadcast'leri) masa listesini anında tazele.
  useEffect(() => {
    if (!companyId) return;
    const callbackId = 'restaurant_screen_table_events';
    pavoSignalRService.onTableUpdated(callbackId, () => loadActiveSales());
    pavoSignalRService.onTableClosed(callbackId, () => loadActiveSales());
    return () => {
      pavoSignalRService.removeTableUpdatedCallback(callbackId);
      pavoSignalRService.removeTableClosedCallback(callbackId);
    };
  }, [companyId, loadActiveSales]);

  // SignalR ile Pavo ödeme durumu değişince listeyi güncelle
  useEffect(() => {
    if (!companyId) return;
    const callbackId = 'restaurant_pavo_tracking';

    pavoSignalRService.onPaymentCompleted(callbackId, () => loadActivePavoPayments());
    pavoSignalRService.onPaymentFailed(callbackId, () => loadActivePavoPayments());
    pavoSignalRService.onPaymentCancelled(callbackId, () => loadActivePavoPayments());

    return () => pavoSignalRService.removeCallbacks(callbackId);
  }, [companyId, loadActivePavoPayments]);

  // ✨ Pavo Restoran Ödendi Onay — Pavo'dan ödeme alındığında SignalR ile anlık masa
  // listesini tazele (10 sn polling beklemeden masa KOYU MOR olsun).
  useEffect(() => {
    if (!companyId) return;
    const callbackId = 'restaurant_pavo_table_closed';
    pavoSignalRService.onTablePavoClosed(callbackId, (payload) => {
      console.log('🟣 [RestaurantScreen] OnTablePavoClosed alındı, aktif satışlar yenileniyor:', payload);
      loadActiveSales();
    });
    return () => pavoSignalRService.removeTablePavoClosedCallback(callbackId);
  }, [companyId, loadActiveSales]);

  // Masa adına göre Pavo ödemesi var mı kontrol eden yardımcı.
  // NOT: Burada SourceType filtresi YOK — masa KOYU MOR indikatörü için
  //   hem `restaurant` (Pavo sipariş modu) hem `quicksale_restp2p` (web'den
  //   gönderilen REST-P2P) ödemeleri "ödeme bekleniyor" sayılır; ödeme
  //   tamamlanmadan masa açılamaz.
  const pavoPaymentsByTable = useMemo(() => {
    const map: Record<string, PavoPaymentRequest> = {};
    activePavoPayments.forEach(p => {
      if (p.sourceReference) {
        const tableName = p.sourceReference.includes('|') ? p.sourceReference.split('|')[0].trim() : p.sourceReference.trim();
        map[tableName] = p;
      }
    });
    return map;
  }, [activePavoPayments]);

  // Pavo Sipariş Takip modalı SADECE Pavo'nun KENDİ sipariş modundan başlatılan
  // ödemeleri listeler. Web tarafından REST-P2P ile gönderilenler
  // (`SourceType === 'quicksale_restp2p'`) bu modalda görünmez — onların yeri
  // QuickSale/Restaurant ödeme akışıdır. Backend'de iki source vardır:
  //   - 'restaurant'         → Pavo cihazının kendi UI'sından sipariş + ödeme
  //   - 'quicksale_restp2p'  → Web'den fatura gönderip Pavo cihazına ödeme alma
  // Filtre: case-insensitive ve trim, çünkü farklı yazımlar gelebilir.
  const pavoTrackingPayments = useMemo(() => {
    return activePavoPayments.filter(p => {
      const src = (p.sourceType || '').toString().trim().toLowerCase();
      return src === 'restaurant';
    });
  }, [activePavoPayments]);

  // ═══════════════════════════════════════════════════════════════════════════════
  // MÜŞTERİ EKRANI (CUSTOMER DISPLAY) - Restoran ekranı için
  // Global window değişkeni kullanarak QuickSaleScreen ile paylaşım sağlanır
  // ═══════════════════════════════════════════════════════════════════════════════
  
  // ✅ PR2 (Invoice Seller Snapshot Refactor): Restaurant ekranı setupDetails geldiğinde
  // backend'den satıcı snapshot'ını çek. QuickSaleScreens.BranchId üzerinden Companies vs
  // Branches.Firm* karar mantığı backend'de; burada sadece tüketim.
  useEffect(() => {
    const screenIdNum = setupDetails?.quickSaleScreenId;
    if (!screenIdNum || screenIdNum <= 0) {
      setSeller(null);
      return;
    }
    let cancelled = false;
    getSellerByScreen(screenIdNum, 'Restaurant')
      .then((snapshot) => {
        if (cancelled) return;
        setSeller(snapshot);
        console.log(
          '[RestaurantScreen] 🏪 Seller snapshot:',
          `${snapshot.sellerCompanyName} (${snapshot.source}, franchise=${snapshot.isFranchiseInvoice})`,
        );
      })
      .catch((err) => {
        if (cancelled) return;
        console.warn('[RestaurantScreen] Seller fetch hatası:', err);
        setSeller(null);
      });
    return () => {
      cancelled = true;
    };
  }, [setupDetails?.quickSaleScreenId]);

  // ✅ RestaurantScreen'e dönüldüğünde müşteri ekranındaki sepeti gizle
  useEffect(() => {
    const screenIdNum = setupDetails?.quickSaleScreenId;
    if (!screenIdNum) return;
    
    // BroadcastChannel ile müşteri ekranına "sepeti gizle" mesajı gönder
    const channelName = `customer-display-${screenIdNum}`;
    const channel = new BroadcastChannel(channelName);
    
    // Sepeti gizle mesajı gönder
    channel.postMessage({
      type: 'HIDE_CART',
      cart: [],
      total: 0,
    });
    console.log('📺 [RestaurantScreen] Müşteri ekranına sepeti gizle mesajı gönderildi');
    
    // Kanalı kapat
    channel.close();
  }, [setupDetails?.quickSaleScreenId]);
  
  // Müşteri ekranını otomatik aç (isEnabled ise)
  useEffect(() => {
    console.log('🔍 [RestaurantScreen] === MÜŞTERİ EKRANI useEffect BAŞLADI ===');
    console.log('🔍 [RestaurantScreen] Değerler:', {
      quickSaleScreenId: setupDetails?.quickSaleScreenId,
      companyId: companyId,
      loading: loading,
      authUser: authUser ? 'VAR' : 'YOK',
      authUserUid: authUser?.uid
    });
    
    // ✅ Bağımlılıkları kontrol et - yoksa çıkış yap
    if (!setupDetails?.quickSaleScreenId || !companyId || loading) {
      console.log('🔍 [RestaurantScreen] Bağımlılıklar eksik, çıkış yapılıyor');
      return;
    }
    
    // ✅ Auth kontrolü - kullanıcı çıkış yapmışsa API çağrısı yapma
    if (!authUser) {
      console.log('📺 [RestaurantScreen] Kullanıcı oturum açmamış, müşteri ekranı açılmıyor');
      return;
    }
    
    // 📺 Electron tespiti — Electron ise ikinci monitörde fullscreen
    // BrowserWindow açılır (multi-monitor); Chrome ise window.open popup.
    // QuickSaleScreen / PackageOrderScreen ile birebir aynı pattern.
    // @ts-ignore
    const electronAPI = window.electronAPI;
    const isElectron = !!electronAPI?.customerDisplay;
    
    // 🛡️ SYNC ÖN-KONTROL (Chrome): Pencere zaten açıksa setTimeout BAŞLATMA.
    // Bu, QuickSaleScreen → RestaurantScreen geri dönüş senaryosunda (useEffect
    // her remount'ta tetiklenir) gereksiz 2.5sn timer + pencere reload riskini
    // tamamen ortadan kaldırır. (Electron için sync alternatif yok; Electron'da
    // sadece async `isOpen()` kontrolü içeride yapılır.)
    if (!isElectron) {
      // @ts-ignore
      const existingWindow = window.__customerDisplayWindow as Window | null;
      if (existingWindow && !existingWindow.closed) {
        console.log('📺 [RestaurantScreen] Pencere zaten açık (sync ön-kontrol), useEffect erken çıkış');
        return;
      }
    }
    
    let isMounted = true;
    
    const checkAndOpenCustomerDisplay = async () => {
      console.log('🔍 [RestaurantScreen] checkAndOpenCustomerDisplay BAŞLADI');
      console.log('🔍 [RestaurantScreen] isMounted:', isMounted);
      console.log('🔍 [RestaurantScreen] authUser (closure):', authUser ? 'VAR' : 'YOK');
      console.log('🔍 [RestaurantScreen] auth.currentUser (güncel):', ownAuthSession.getCurrentUid() ? 'VAR' : 'YOK');
      
      // ✅ Her adımda isMounted kontrolü yap
      if (!isMounted) {
        console.log('🔍 [RestaurantScreen] isMounted=false, çıkış');
        return;
      }
      
      // ✅ Güncel auth kontrolü - closure'daki değil, Firebase'deki güncel değeri kontrol et
      const currentAuthUid = ownAuthSession.getCurrentUid();
      if (!currentAuthUid) {
        console.log('🔍 [RestaurantScreen] auth.currentUser null, çıkış (oturum kapanmış olabilir)');
        return;
      }
      
      const screenIdNum = setupDetails.quickSaleScreenId;
      
      // ✅ Pencere zaten açıksa tekrar açma — sadece Chrome modunda
      // (Electron'da `__customerDisplayWindow` kullanılmaz; Electron'un
      // kendi `customerDisplay.isOpen()` çağrısıyla içeride kontrol ederiz.)
      if (!isElectron) {
        // @ts-ignore
        const existingWindow = window.__customerDisplayWindow as Window | null;
        if (existingWindow && !existingWindow.closed) {
          console.log('📺 [RestaurantScreen] Pencere zaten açık (global)');
          return;
        }
      }
      
      // ✅ Tüm işlemi try-catch içine al
      try {
        console.log('📺 [RestaurantScreen] Müşteri ekranı ayarları kontrol ediliyor...');
        console.log('🔍 [RestaurantScreen] API çağrısı öncesi authUser:', authUser ? 'VAR' : 'YOK');
        
        // API çağrısı
        let isEnabled = false;
        try {
          const response = await getCustomerDisplaySettings(screenIdNum!);
          console.log('🔍 [RestaurantScreen] API yanıtı:', response?.Success, 'isMounted:', isMounted);
          console.log('🔍 [RestaurantScreen] API sonrası auth.currentUser:', ownAuthSession.getCurrentUid() ? 'VAR' : 'YOK');
          
          // ✅ API sonrası auth ve isMounted kontrolü
          if (!isMounted || !ownAuthSession.getCurrentUid()) {
            console.log('🔍 [RestaurantScreen] API sonrası durum geçersiz, çıkış', { isMounted, hasAuth: !!ownAuthSession.getCurrentUid() });
            return;
          }
          isEnabled = response.Success && response.Data?.IsEnabled === true;
          console.log('🔍 [RestaurantScreen] isEnabled:', isEnabled);
        } catch (apiErr: any) {
          console.warn('📺 [RestaurantScreen] API hatası (devam ediliyor):', apiErr);
          console.warn('🔍 [RestaurantScreen] API Hata Detayı:', {
            message: apiErr?.message,
            status: apiErr?.response?.status,
            statusText: apiErr?.response?.statusText
          });
          return; // API hatası varsa çık, ama oturum kapatma
        }
        
        if (!isEnabled) {
          console.log('📺 [RestaurantScreen] Müşteri ekranı pasif veya ayarlar bulunamadı');
          return;
        }
        
        console.log('📺 [RestaurantScreen] Müşteri ekranı aktif, pencere açılıyor...');
        
        // ✅ İkinci ekran bilgisini almaya çalış (Screen API)
        let targetLeft = window.screen.width; // Varsayılan: birincil ekranın sağı
        let targetTop = 0;
        let targetWidth = window.screen.width;
        let targetHeight = window.screen.height;
        
        // Screen API - ayrı try-catch içinde (hata olursa varsayılan kullan)
        // ✅ DEBUG: Screen API çağrısı öncesi isMounted ve auth kontrolü
        console.log('🔍 [RestaurantScreen] Screen API öncesi durum:', { isMounted, authUserExists: !!authUser });
        
        try {
          if ('getScreenDetails' in window) {
            console.log('📺 [RestaurantScreen] Screen API destekleniyor, ekranlar alınıyor...');
            
            // ✅ DEBUG: Screen API çağrısı anında auth kontrolü (güncel değer)
            if (!ownAuthSession.getCurrentUid()) {
              console.warn('⚠️ [RestaurantScreen] Screen API öncesi auth.currentUser kayboldu! Çıkış yapılıyor.');
              return;
            }
            
            // @ts-ignore
            const screenDetails = await (window as any).getScreenDetails();
            console.log('🔍 [RestaurantScreen] Screen API sonucu alındı, isMounted:', isMounted);
            console.log('🔍 [RestaurantScreen] Screen API sonrası auth.currentUser:', ownAuthSession.getCurrentUid() ? 'VAR' : 'YOK');
            
            // ✅ DEBUG: Screen API sonrası auth kontrolü (güncel değer)
            if (!ownAuthSession.getCurrentUid()) {
              console.warn('⚠️ [RestaurantScreen] Screen API sonrası auth.currentUser kayboldu! Çıkış yapılıyor.');
              return;
            }
            
            if (!isMounted) {
              console.log('🔍 [RestaurantScreen] Screen API sonrası isMounted=false, çıkış');
              return;
            }
            
            const screens = screenDetails?.screens;
            if (screens && screens.length > 0) {
              console.log('📺 [RestaurantScreen] Bulunan ekranlar:', screens.length);
              
              const secondaryScreen = screens.find((s: any) => !s.isPrimary);
              if (secondaryScreen) {
                targetLeft = secondaryScreen.left || targetLeft;
                targetTop = secondaryScreen.top || 0;
                targetWidth = secondaryScreen.width || targetWidth;
                targetHeight = secondaryScreen.height || targetHeight;
                console.log('📺 [RestaurantScreen] ✅ İKİNCİ EKRAN BULUNDU:', {
                  left: targetLeft, top: targetTop, width: targetWidth, height: targetHeight
                });
              } else {
                console.log('📺 [RestaurantScreen] İkinci ekran bulunamadı, birincil ekranın sağına açılacak');
              }
            }
          } else {
            console.log('📺 [RestaurantScreen] Screen API desteklenmiyor, birincil ekranın sağına açılacak');
          }
        } catch (screenErr: any) {
          console.warn('📺 [RestaurantScreen] Ekran bilgisi alınamadı:', screenErr);
          console.warn('🔍 [RestaurantScreen] Screen API Hata Detayı:', screenErr?.message);
          // Hata olursa varsayılan değerlerle devam et - logout tetikleme!
        }
        
        if (!isMounted) {
          console.log('🔍 [RestaurantScreen] Pencere açma öncesi isMounted=false, çıkış');
          return;
        }
        
        console.log('🔍 [RestaurantScreen] Pencere açılmaya çalışılıyor...');
        
        // ✅ CompanyId'yi localStorage'a yaz (URL'de gösterme - güvenlik)
        localStorage.setItem('__customerDisplayCompanyId', companyId);
        
        // Auth token'ı cache'le (yeni müşteri ekranı penceresinde firebase.ts auth'u
        // initialize ETMEZ; apiClient bu token'ı Authorization header için fallback olarak
        // kullanır. Cache yoksa CustomerDisplayScreen'in API çağrıları 401 döner.)
        try {
          const idToken = await ownAuthSession.getAccessToken();
          if (idToken) {
            localStorage.setItem('__customerDisplayAuthToken', idToken);
            console.log('📺 [CustomerDisplay] Auth token cache\'lendi');
          }
        } catch (tokenErr) {
          console.warn('📺 [CustomerDisplay] Auth token cache\'lenemedi:', tokenErr);
        }
        
        const displayPath = `/#/customer-display/${screenIdNum}`;
        
        // ========== ELECTRON: İkinci monitörde fullscreen BrowserWindow ==========
        // QuickSaleScreen / PackageOrderScreen ile birebir aynı pattern.
        // Electron preload'u multi-monitor algılayıp fullscreen açar; Chrome
        // popup'ın aksine ikinci ekran ayarlama / popup blocker derdi yok.
        if (isElectron) {
          console.log('📺 [RestaurantScreen] Electron algılandı, ikinci monitörde açılıyor...');
          try {
            const statusRes = await electronAPI.customerDisplay.isOpen();
            if (statusRes?.isOpen) {
              console.log('📺 [RestaurantScreen] Electron penceresi zaten açık, atlanıyor...');
              return;
            }
          } catch (e) {
            // isOpen başarısız olursa açmayı dene; kritik değil
          }
          
          if (!isMounted || !ownAuthSession.getCurrentUid()) {
            console.log('📺 [RestaurantScreen] Electron isOpen sonrası durum geçersiz, çıkış');
            return;
          }
          
          const baseUrl = window.location.origin || 'http://localhost:3003';
          const fullUrl = `${baseUrl}${displayPath}`;
          
          try {
            const result = await electronAPI.customerDisplay.open(fullUrl);
            if (result?.success) {
              console.log('✅ [RestaurantScreen] Electron ikinci monitörde müşteri ekranı açıldı');
            } else {
              console.warn('⚠️ [RestaurantScreen] Electron müşteri ekranı açılamadı');
            }
          } catch (err) {
            console.error('❌ [RestaurantScreen] Electron müşteri ekranı hatası:', err);
          }
          return;
        }
        
        // ========== CHROME: window.open ile popup ==========
        const url = displayPath;
        const windowFeatures = [
          `width=${targetWidth}`,
          `height=${targetHeight}`,
          `left=${targetLeft}`,
          `top=${targetTop}`,
          'menubar=no',
          'toolbar=no',
          'location=no',
          'status=no',
          'resizable=yes',
          'scrollbars=no',
        ].join(',');
        
        console.log('🔍 [RestaurantScreen] window.open çağrılıyor:', { url, windowFeatures: windowFeatures.substring(0, 100) + '...' });
        console.log('🔍 [RestaurantScreen] window.open öncesi auth.currentUser:', ownAuthSession.getCurrentUid() ? 'VAR' : 'YOK');
        
        let newWindow: Window | null = null;
        try {
          newWindow = window.open(url, 'CustomerDisplay', windowFeatures);
          console.log('🔍 [RestaurantScreen] window.open sonucu:', newWindow ? 'Pencere açıldı' : 'null döndü');
          console.log('🔍 [RestaurantScreen] window.open sonrası auth.currentUser:', ownAuthSession.getCurrentUid() ? 'VAR' : 'YOK');
        } catch (openErr: any) {
          console.error('❌ [RestaurantScreen] window.open HATA:', openErr?.message);
          return;
        }
        
        // ✅ window.open sonrası auth kontrolü
        if (!ownAuthSession.getCurrentUid()) {
          console.warn('⚠️ [RestaurantScreen] window.open sonrası auth.currentUser kayboldu!');
          // Pencereyi kapat (açıldıysa)
          if (newWindow && !newWindow.closed) {
            try { newWindow.close(); } catch (e) { /* ignore */ }
          }
          return;
        }
        
        if (newWindow && isMounted) {
          // @ts-ignore
          window.__customerDisplayWindow = newWindow;
          console.log('✅ [RestaurantScreen] Müşteri ekranı penceresi açıldı ve kaydedildi');
          
          // Pencere konumlandırma
          setTimeout(() => {
            try {
              if (newWindow && !newWindow.closed) {
                newWindow.moveTo(targetLeft, targetTop);
                newWindow.resizeTo(targetWidth, targetHeight);
                console.log('📺 [RestaurantScreen] Pencere konumlandırıldı');
              }
            } catch (posErr) {
              console.warn('📺 [RestaurantScreen] Pencere ayarlanamadı:', posErr);
            }
          }, 500);
        } else if (!newWindow) {
          console.warn('⚠️ [RestaurantScreen] Pencere açılamadı (popup blocker?)');
          // Popup blocker durumunda hiçbir şey yapma, sadece log
        } else if (!isMounted) {
          console.log('🔍 [RestaurantScreen] Pencere açıldı ama component unmount olmuş');
        }
        
        console.log('🔍 [RestaurantScreen] checkAndOpenCustomerDisplay TAMAMLANDI');
      } catch (err: any) {
        // ✅ Genel hata - ASLA oturum kapatma
        console.error('❌ [RestaurantScreen] Müşteri ekranı açılırken hata (oturum etkilenmeyecek):', err);
        console.error('🔍 [RestaurantScreen] Hata Detayı:', {
          message: err?.message,
          stack: err?.stack?.substring(0, 200)
        });
      }
    };
    
    // ✅ Daha uzun gecikme - auth hazır olsun
    console.log('🔍 [RestaurantScreen] setTimeout başlatılıyor (2500ms)');
    const timer = setTimeout(checkAndOpenCustomerDisplay, 2500);
    
    return () => {
      console.log('🔍 [RestaurantScreen] === MÜŞTERİ EKRANI useEffect CLEANUP ===');
      isMounted = false;
      clearTimeout(timer);
    };
  }, [setupDetails?.quickSaleScreenId, companyId, loading, authUser]);
  
  // ✅ Satış modunda olduğumuzu işaretle (RestaurantScreen veya QuickSaleScreen)
  //
  // 🛡️ KRİTİK YARIŞ DURUMU FIX'İ:
  // React 18 StrictMode (dev) veya HMR (hot reload) sırasında bu useEffect
  // mount → cleanup → mount cycle yaşar. Cleanup zamanında `__inSalesMode`
  // hâlâ 'restaurant' (kendisi set etti), `__navigatingToQuickSale` da yok
  // → "tam çıkış" sanılıp pencere kapatılırdı → ikinci mount'ta 707'nin sync
  // ön-kontrolü closed/null görüp 2.5sn sonra YENİDEN AÇIYORDU.
  //
  // Çözüm: Kapatma'yı 250ms DEFER et. Bu süre içinde başka bir mount olursa
  // (StrictMode pretend-unmount, HMR re-run, veya QuickSaleScreen → Restaurant
  // geri dönüşünde sırada gelen yeni mount), kapatma İPTAL edilir.
  useEffect(() => {
    sessionStorage.setItem('__inSalesMode', 'restaurant');
    console.log('📺 [RestaurantScreen] Satış moduna girildi: restaurant');
    
    return () => {
      const isNavigatingToQuickSale = sessionStorage.getItem('__navigatingToQuickSale');
      const currentMode = sessionStorage.getItem('__inSalesMode');
      
      if (isNavigatingToQuickSale === 'true') {
        console.log('📺 [RestaurantScreen] QuickSaleScreen\'e geçiş yapılıyor, müşteri ekranı kapatılmayacak');
        sessionStorage.removeItem('__navigatingToQuickSale');
        return;
      }
      
      if (currentMode === 'quicksale') {
        console.log('📺 [RestaurantScreen] QuickSaleScreen\'den geri dönülüyor, müşteri ekranı kapatılmayacak');
        return;
      }
      
      // 🛡️ DEFER kapatma: 250ms sonra hâlâ tam çıkış mı kontrol et
      setTimeout(() => {
        const stillModeAfterDelay = sessionStorage.getItem('__inSalesMode');
        // Eğer arada başka bir RestaurantScreen veya QuickSaleScreen mount olduysa
        // (StrictMode/HMR/yeniden mount), kapatma iptal — yeni mount onu yönetir.
        if (stillModeAfterDelay === 'restaurant' || stillModeAfterDelay === 'quicksale') {
          console.log('📺 [RestaurantScreen] Yeniden mount tespit edildi, müşteri ekranı kapatma İPTAL', {
            mode: stillModeAfterDelay,
          });
          return;
        }
        
        sessionStorage.removeItem('__inSalesMode');
        
        // @ts-ignore
        const electronAPI = window.electronAPI;
        if (electronAPI?.customerDisplay) {
          try {
            electronAPI.customerDisplay.close();
            console.log('📺 [RestaurantScreen] Electron müşteri ekranı kapatma isteği gönderildi (defer)');
          } catch (err) {
            console.warn('📺 [RestaurantScreen] Electron kapatma hatası:', err);
          }
        } else {
          // @ts-ignore
          const customerWindow = window.__customerDisplayWindow as Window | null;
          if (customerWindow && !customerWindow.closed) {
            console.log('📺 [RestaurantScreen] Tam çıkış (defer doğrulandı), müşteri ekranı kapatılıyor...');
            try {
              customerWindow.close();
            } catch (err) {
              console.warn('📺 [RestaurantScreen] Pencere kapatılamadı:', err);
            }
            // @ts-ignore
            window.__customerDisplayWindow = null;
          }
        }
      }, 250);
    };
  }, []);

  const handleTableClick = (table: RestaurantTableData) => {
    if (!setupId || !table.id || !setupDetails || !restaurantLoginUser) {
      console.error("Gerekli bilgiler eksik:", { setupId, tableId: table.id, setupDetails, restaurantLoginUser });
      showSnackbar(t('restaurantScreen.errors.openTableFailed'), 'error');
      return;
    }

    // ✅ KRITIK: QuickSaleScreenId kontrolü
    if (!setupDetails.quickSaleScreenId) {
      console.error("❌ QuickSaleScreenId bulunamadı!", setupDetails);
      showSnackbar(t('restaurantScreen.errors.quickSaleNotFound'), 'error');
      return;
    }

    // ✨ Pavo Restoran Ödendi Onay — KOYU MOR masa: navigate yerine info modal aç
    const activeSale = activeSales[table.id];
    if (activeSale?.PavoPaymentPending) {
      console.log(
        '🟣 [Pavo Onay] Pavo\'dan ödenmiş masaya tıklandı, info modal açılıyor:',
        { tableId: table.id, tableName: table.name, activeSaleId: activeSale.Id },
      );
      setPavoClosedInfoModal({
        open: true,
        sale: activeSale as RestaurantActiveSaleDto,
        tableName: table.name,
      });
      return;
    }

    // ✅ YENİ: QuickSaleScreen'e restaurant modu ile yönlendir
    // Garson veya kasiyer bilgisini gönder
    const userType = restaurantLoginUser.loginType === 'cashier' ? 'KASİYER' : 'GARSON';
    console.log(`🍽️ [RestaurantScreen] Masaya tıklandı: ${table.name}, ${userType}: ${restaurantLoginUser.name}`);
    
    // ✅ QuickSaleScreen'e geçiş yapılıyor flag'i - müşteri ekranı kapatılmasın
    sessionStorage.setItem('__navigatingToQuickSale', 'true');
    
    // ✅ DOĞRU: quickSaleScreenId kullan (setupId değil!)
    navigate(`/hizli-satis/ekran/${setupDetails.quickSaleScreenId}`, {
      state: {
        isRestaurantMode: true,
        restaurantScreenId: setupId,
        restaurantBranchId: setupDetails.branchId || null,
        tableId: table.id,
        tableName: table.name,
        useKitchenScreen: setupDetails.useKitchenScreen ?? false,
        // ✅ YENİ: Garson veya Kasiyer bilgisi
        loginInfo: {
          id: restaurantLoginUser.id,
          name: restaurantLoginUser.name,
          loginType: restaurantLoginUser.loginType, // 'waiter' | 'cashier'
          // Ortak yetkiler
          canCloseSale: restaurantLoginUser.canCloseSale,
          canCloseTable: restaurantLoginUser.canCloseTable,
          canAddProduct: restaurantLoginUser.canAddProduct,
          canRemoveProduct: restaurantLoginUser.canRemoveProduct,
          canClearCart: restaurantLoginUser.canClearCart,
          canPrintToKitchen: restaurantLoginUser.canPrintToKitchen,
          canDeletePrintedItems: restaurantLoginUser.canDeletePrintedItems,
          canDeleteUnprintedItems: restaurantLoginUser.canDeleteUnprintedItems, // ✅ YENİ: Yazdırılmamış ürün silme yetkisi
          canTransferTable: restaurantLoginUser.canTransferTable,
          canPosRefund: restaurantLoginUser.canPosRefund, // ✅ YENİ: POS İade yetkisi
          canCancelReceipt: restaurantLoginUser.canCancelReceipt, // ✅ YENİ: Adisyon iptal yetkisi
          canCancelUnprintedReceipt: restaurantLoginUser.canCancelUnprintedReceipt, // ✅ YENİ: Yazdırılmamış adisyon iptal yetkisi
          canPrintReceipt: restaurantLoginUser.canPrintReceipt, // ✅ YENİ: Adisyon yazdırma yetkisi
          canAddProductAfterReceiptPrint: restaurantLoginUser.canAddProductAfterReceiptPrint, // ✅ YENİ: Adisyon sonrası ürün ekleme
          canUsePavoP2P: restaurantLoginUser.canUsePavoP2P,
          // Kasiyer ek yetkileri (sadece kasiyer girişinde dolu)
          canChangePrice: restaurantLoginUser.canChangePrice,
          canApplyDiscount: restaurantLoginUser.canApplyDiscount,
          canCancelSale: restaurantLoginUser.canCancelSale,
          canViewReports: restaurantLoginUser.canViewReports,
          canViewEndOfDay: restaurantLoginUser.canViewEndOfDay,
          canWarehouseOperations: restaurantLoginUser.canWarehouseOperations,
          canViewAccountBalance: restaurantLoginUser.canViewAccountBalance,
          canListAccounts: restaurantLoginUser.canListAccounts,
          canQuickAddItem: restaurantLoginUser.canQuickAddItem,
          canAddAccount: restaurantLoginUser.canAddAccount,
        },
        // ✅ Geriye uyumluluk için waiterInfo (eski kod için)
        waiterInfo: {
          waiterId: restaurantLoginUser.id,
          waiterName: restaurantLoginUser.name,
          canCloseSale: restaurantLoginUser.canCloseSale,
          canCloseTable: restaurantLoginUser.canCloseTable,
          canAddProduct: restaurantLoginUser.canAddProduct,
          canRemoveProduct: restaurantLoginUser.canRemoveProduct,
          canClearCart: restaurantLoginUser.canClearCart,
          canPrintToKitchen: restaurantLoginUser.canPrintToKitchen,
          canDeletePrintedItems: restaurantLoginUser.canDeletePrintedItems,
          canDeleteUnprintedItems: restaurantLoginUser.canDeleteUnprintedItems, // ✅ YENİ: Yazdırılmamış ürün silme yetkisi
          canTransferTable: restaurantLoginUser.canTransferTable,
          canCancelReceipt: restaurantLoginUser.canCancelReceipt, // ✅ YENİ: Adisyon iptal yetkisi
          canCancelUnprintedReceipt: restaurantLoginUser.canCancelUnprintedReceipt, // ✅ YENİ: Yazdırılmamış adisyon iptal yetkisi
          canPrintReceipt: restaurantLoginUser.canPrintReceipt, // ✅ YENİ: Adisyon yazdırma yetkisi
          canAddProductAfterReceiptPrint: restaurantLoginUser.canAddProductAfterReceiptPrint, // ✅ YENİ: Adisyon sonrası ürün ekleme
          canUsePavoP2P: restaurantLoginUser.canUsePavoP2P,
        }
      }
    });
  };

  // Kullanıcı menüsü handler'ları
  const handleOpenUserMenu = (event: React.MouseEvent<HTMLElement>) => {
    setAnchorElUser(event.currentTarget);
  };

  const handleCloseUserMenu = () => {
    setAnchorElUser(null);
  };

  const handleLogout = async () => {
    try {
      // Hedef rota çıkıştan ÖNCE hesaplanır: terminal oturumu tespiti oturumdaki
      // kimliğe bakıyor ve logout onu siliyor.
      const target = getPostLogoutRoute();
      await logout();
      handleCloseUserMenu();
      navigate(target);
    } catch (error) {
      console.error("Çıkış yapılırken hata oluştu:", error);
    }
  };

  // Tam ekran toggle fonksiyonu
  const toggleFullscreen = useCallback(() => {
    // Electron ortamında IPC üzerinden fullscreen yap
    const electronAPI = (window as any).electronAPI;
    if (electronAPI?.send) {
      electronAPI.send('toggle-fullscreen');
      return;
    }
    
    if (!document.fullscreenElement) {
      document.documentElement.requestFullscreen().catch((err) => {
        console.error('Tam ekran modu etkinleştirilemedi:', err);
      });
    } else {
      document.exitFullscreen();
    }
  }, []);

  // Ekranı kilitle
  const handleLockScreen = useCallback(() => {
    // Tam ekrana geçmeyi dene (sadece fullscreen değilse)
    const electronAPI = (window as any).electronAPI;
    if (electronAPI?.send) {
      if (!isFullscreen) {
        electronAPI.send('toggle-fullscreen');
      }
    } else if (!document.fullscreenElement) {
      document.documentElement.requestFullscreen().catch(() => {});
    }
    
    setIsLocked(true);
    setRestaurantLoginUser(null);
    
    if (setupIdParam) {
      sessionStorage.removeItem(`restaurant_user_${setupIdParam}`);
    }
    
    console.log('🔒 Ekran kilitlendi');
  }, [setupIdParam]);

  // Ekranı aç (garson veya kasiyer girişi başarılı)
  const handleUnlock = (loginInfo: RestaurantLoginInfo) => {
    setRestaurantLoginUser(loginInfo);
    setIsLocked(false);
    
    // Session'a kaydet (QuickSale'e gidip dönünce tekrar kilit açılmasın)
    if (setupIdParam) {
      sessionStorage.setItem(`restaurant_user_${setupIdParam}`, JSON.stringify(loginInfo));
    }
    
    const userType = loginInfo.loginType === 'cashier' ? 'KASİYER' : 'GARSON';
    console.log(`✅ ${userType} girişi başarılı (SQL'den güncel yetkiler):`, loginInfo.name);
  };

  // Oto kilitleme hook
  const autoLockHookEnabled = autoLockEnabled && !isLocked && !!restaurantLoginUser && !isLayoutEditMode;

  const {
    showCountdown: showAutoLockCountdown,
    dismissCountdown: dismissAutoLockCountdown,
  } = useAutoLock({
    enabled: autoLockHookEnabled,
    timeoutSeconds: autoLockTimeoutSeconds,
    onLock: handleLockScreen,
  });

  // QuickSaleScreen'den autoLockTriggered ile geri dönüldüğünde otomatik kilitle
  useEffect(() => {
    const state = location.state as any;
    if (state?.autoLockTriggered) {
      handleLockScreen();
      // State'i temizle (geri/ileri ile tekrar tetiklenmesin)
      navigate(location.pathname, { replace: true, state: {} });
    }
  }, [location.state]);

  // Tam ekran değişikliklerini ve F11 tuşunu dinle
  useEffect(() => {
    const handleFullscreenChange = () => {
      setIsFullscreen(!!document.fullscreenElement);
    };

    // Electron IPC üzerinden fullscreen state değişikliğini dinle
    const handleElectronFullscreenChange = (e: any) => {
      setIsFullscreen(e.detail?.isFullScreen ?? false);
    };

    const handleKeyDown = (event: KeyboardEvent) => {
      if (event.key === 'F11') {
        event.preventDefault();
        toggleFullscreen();
      }
    };

    document.addEventListener('fullscreenchange', handleFullscreenChange);
    window.addEventListener('electron-fullscreen-change', handleElectronFullscreenChange);
    document.addEventListener('keydown', handleKeyDown);
    
    return () => {
      document.removeEventListener('fullscreenchange', handleFullscreenChange);
      window.removeEventListener('electron-fullscreen-change', handleElectronFullscreenChange);
      document.removeEventListener('keydown', handleKeyDown);
    };
  }, [toggleFullscreen]);

  // ✅ YENİ: Sayfa yenilendiğinde (F5) sessionStorage'ı temizle
  useEffect(() => {
    const handleBeforeUnload = () => {
      if (setupIdParam) {
        sessionStorage.removeItem(`restaurant_user_${setupIdParam}`);
      }
    };

    window.addEventListener('beforeunload', handleBeforeUnload);
    
    return () => {
      window.removeEventListener('beforeunload', handleBeforeUnload);
    };
  }, [setupIdParam]);

  // Doğal sıralama (Natural Sort) - "Masa 1, Masa 2, ..., Masa 10" şeklinde
  const naturalSort = (a: RestaurantTableData, b: RestaurantTableData) => {
    return a.name.localeCompare(b.name, undefined, { numeric: true, sensitivity: 'base' });
  };

  // Tüm masaları sıralı tutmak için memoize edilmiş liste
  const sortedAllTables = useMemo(() => {
    return [...allTables].sort(naturalSort);
  }, [allTables]);

  const getTablesForGroup = useCallback((groupId: string): RestaurantTableData[] => {
    if (groupId === 'all') return sortedAllTables;
    const group = tableGroups.find(g => g.id === groupId);
    if (!group) return [];
    return allTables
      .filter(t => t.groupId === groupId)
      .sort((a, b) => a.name.localeCompare(b.name, undefined, { numeric: true, sensitivity: 'base' }));
  }, [sortedAllTables, allTables, tableGroups]);

  const filteredGroupedTables = useMemo(() => {
    const groupsMap: { [groupId: string]: { groupName: string; tables: RestaurantTableData[] } } = {};
    let groupsToProcess = tableGroups;

    if (selectedGroupIdFilter !== 'all') {
        groupsToProcess = tableGroups.filter(g => g.id === selectedGroupIdFilter);
    }

    groupsToProcess.forEach(group => {
      groupsMap[group.id] = {
        groupName: group.name,
        tables: allTables.filter(table => table.groupId === group.id)
                         .sort(naturalSort) // ✅ Doğal sıralama
      };
    });

    if (selectedGroupIdFilter === 'all') {
        const ungrouped = allTables.filter(table => !table.groupId || !tableGroups.some(g => g.id === table.groupId));
        if (ungrouped.length > 0) {
          groupsMap['ungrouped_tables'] = {
            groupName: 'Diğer Masalar',
            tables: ungrouped.sort(naturalSort) // ✅ Doğal sıralama
          };
        }
    }
    return groupsMap;
  }, [allTables, tableGroups, selectedGroupIdFilter]);

  const handleFilterChange = (
    event: React.MouseEvent<HTMLElement>,
    newAlignment: string | null,
  ) => {
    if (newAlignment !== null) {
      setSelectedGroupIdFilter(newAlignment);
      if (setupIdParam) {
        sessionStorage.setItem(`restaurant_group_${setupIdParam}`, newAlignment);
      }
    }
  };

  const getTableCardBackgroundColor = (table: RestaurantTableData) => {
    const activeSale = activeSales[table.id];

    // ✨ Pavo Restoran Ödendi Onay — Pavo'dan ödendi, web kasiyer onayı bekliyor
    // (Bu kontrol Pavo P2P ödeme bekleme kontrolünden ÖNCE gelmeli — daha kalıcı bir durum.)
    if (activeSale?.PavoPaymentPending) {
      return '#6A1B9A'; // KOYU MOR
    }

    // Pavo P2P ödemesi gönderilmiş mi?
    if (pavoPaymentsByTable[table.name]) {
      return '#9C27B0'; // mor (P2P bekleyen)
    }

    if (!activeSale) {
      return 'primary.main'; // Boş masa - mavi
    }
    
    if (activeSale.IsReceiptPrinted) {
      return 'warning.main'; // Adisyon yazdırılmış - SARI
    }
    
    return 'success.main'; // Aktif masa - yeşil
  };

  // ✅ YENİ: Masa kartı için "kaç dakikadır açık" rakamı.
  // Sadece ayar açıkken ve aktif satışı olan masalarda gösterilir.
  // Tasarım kararı: ikon yok, yalnızca rakam — kullanıcı isteği.
  //
  // 🔧 OpenedAt PARSE STRATEJİSİ:
  // Backend `OpenedAt`'i ISO 8601 olarak göndermesi BEKLENİYOR. Ancak .NET tarafı
  // `DateTime.UtcNow` veya benzeri bir UTC değeri saklayıp serializasyonda Z/offset
  // EKLEMEYEBİLİYOR ("2026-05-20T07:44:00" gibi). Tarayıcı bu naive string'i local
  // time olarak parse edip 180 dk (UTC+3 = TR offset) hatalı fark üretiyordu.
  // Düzeltme: Timezone marker'ı yoksa (Z, +HH:MM, -HH:MM), string'i UTC olarak
  // yorumla → "Z" ekleyip parse et. Marker varsa olduğu gibi parse et.
  const parseOpenedAtToMs = (raw: string): number => {
    if (!raw || typeof raw !== 'string') return NaN;
    const hasTimezoneMarker = /Z$|[+-]\d{2}:?\d{2}$/.test(raw.trim());
    const normalized = hasTimezoneMarker ? raw : raw + 'Z';
    return new Date(normalized).getTime();
  };

  // Her masa için sadece İLK render'da debug log at (spam önle)
  const openDurationLoggedRef = useRef<Set<string>>(new Set());

  const renderTableOpenDurationLabel = (table: RestaurantTableData): JSX.Element | null => {
    if (!showTableOpenDuration) return null;
    const sale = activeSales[table.id];
    if (!sale?.OpenedAt) {
      // İlk seferde aktif satış yoksa logla (boş masada zaten render etmeyiz)
      return null;
    }
    const rawOpenedAt: string = sale.OpenedAt;
    const openedAtMs = parseOpenedAtToMs(rawOpenedAt);

    // 🐛 DEBUG: Her masa için tek bir kez detaylı log — sorun teşhisi
    const tableKey = String(table.id);
    if (!openDurationLoggedRef.current.has(tableKey)) {
      openDurationLoggedRef.current.add(tableKey);
      const naiveParsedMs = new Date(rawOpenedAt).getTime();
      console.log('🕐 [MasaSüresi DEBUG]', {
        tableId: table.id,
        tableName: table.name,
        rawOpenedAt,
        hasTimezoneMarker: /Z$|[+-]\d{2}:?\d{2}$/.test((rawOpenedAt || '').trim()),
        parsedOpenedAtMs: openedAtMs,
        parsedOpenedAtUTC: Number.isFinite(openedAtMs) ? new Date(openedAtMs).toISOString() : 'NaN',
        parsedOpenedAtLocal: Number.isFinite(openedAtMs) ? new Date(openedAtMs).toString() : 'NaN',
        naiveParsedMs,
        naiveDiffMin: Math.floor((Date.now() - naiveParsedMs) / 60000),
        nowMs: Date.now(),
        nowUTC: new Date().toISOString(),
        nowLocal: new Date().toString(),
        finalDiffMin: Math.floor((Date.now() - openedAtMs) / 60000),
        browserTimezone: Intl.DateTimeFormat().resolvedOptions().timeZone,
        browserTzOffsetMin: new Date().getTimezoneOffset(),
      });
    }

    if (Number.isNaN(openedAtMs)) {
      console.warn('🕐 [MasaSüresi] OpenedAt parse edilemedi:', { tableId: table.id, rawOpenedAt });
      return null;
    }
    // HH:MM formatı (00:01, 00:59, 01:00, 01:01 ...)
    // Negatif (gelecek) ise 00:00 göster.
    const diffMs = openDurationTick - openedAtMs;
    const safeDiffMs = diffMs < 0 ? 0 : diffMs;
    const totalMinutes = Math.floor(safeDiffMs / 60000);
    const hh = Math.floor(totalMinutes / 60);
    const mm = totalMinutes % 60;
    const formatted = `${hh.toString().padStart(2, '0')}:${mm.toString().padStart(2, '0')}`;
    return (
      <Typography
        component="span"
        sx={{
          color: 'white',
          fontSize: `${0.7 * tableNameFontScale}rem`,
          fontWeight: 700,
          lineHeight: 1,
          opacity: 0.95,
          mt: 0.3,
          fontVariantNumeric: 'tabular-nums',
          letterSpacing: '0.5px',
        }}
      >
        {formatted}
      </Typography>
    );
  };

  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down('sm'));
  const isSmall = useMediaQuery(theme.breakpoints.down('md'));

  const [drawerOpen, setDrawerOpen] = useState(false);
  const [mobileMenuAnchor, setMobileMenuAnchor] = useState<null | HTMLElement>(null);
  const [leftDrawerOpen, setLeftDrawerOpen] = useState(false);

  const toggleDrawer = (open: boolean) => () => {
    setDrawerOpen(open);
  };

  const openMobileMenu = (event: React.MouseEvent<HTMLElement>) => setMobileMenuAnchor(event.currentTarget);
  const closeMobileMenu = () => setMobileMenuAnchor(null);
  const toggleLeftDrawer = (open: boolean) => () => setLeftDrawerOpen(open);

  const handleDrawerTableClick = (table: RestaurantTableData) => {
    handleTableClick(table);
    setDrawerOpen(false);
  };

  if (loading) {
    return (
      <Box display="flex" justifyContent="center" alignItems="center" minHeight="calc(100vh - 64px)">
        <CircularProgress />
        <Typography sx={{ml: 2}}>{t('restaurantScreen.loading')}</Typography>
      </Box>
    );
  }

  if (error && !setupDetails) {
    return (
      <Container sx={{mt:3}}>
        {isAdmin && (
          <Button startIcon={<ArrowBackIcon />} onClick={() => navigate('/restaurant-management')} sx={{mb:2}}>
            {t('restaurantScreen.menu.backToRestaurantScreens')}
          </Button>
        )}
        <Alert severity="error">{error}</Alert>
      </Container>
    );
  }

  if (!setupDetails && !loading) {
    return (
      <Container sx={{mt:3}}>
        {isAdmin && (
          <Button startIcon={<ArrowBackIcon />} onClick={() => navigate('/restaurant-management')} sx={{mb:2}}>
            {t('restaurantScreen.menu.backToRestaurantScreens')}
          </Button>
        )}
        <Alert severity="warning">{t('restaurantScreen.detailsMissing')}</Alert>
        {error && <Alert severity="error" sx={{mt: 1}}>{error}</Alert>}
      </Container>
    );
  }

  const groupOrder = Object.keys(filteredGroupedTables);

  return (
    <>
      {/* Oto Kilitleme Geri Sayım Modalı */}
      <AutoLockCountdownModal
        open={showAutoLockCountdown}
        onDismiss={dismissAutoLockCountdown}
        onTimeout={handleLockScreen}
      />

      {/* 🔒 Kilit Ekranı Modal - Sadece şifre ile açılır! */}
      {setupId && (
        <RestaurantLockScreenModal
          open={isLocked}
          restaurantScreenId={setupId}
          onUnlock={handleUnlock}
        />
      )}

      {/* ✅ YENİ: Kasiyere özel Gün Sonu Yazdırma Modalı (ekran şubesine kilitli) */}
      <EndOfDayPrintModal
        open={endOfDayPrintModalOpen}
        onClose={() => setEndOfDayPrintModalOpen(false)}
        lockedBranchId={setupDetails?.branchId || null}
      />

      <Box sx={{ width: '100vw', minHeight: '100vh', bgcolor: '#f7f8fa', overflowY: 'auto' }}>
        {/* ✅ YENİ: QuickSaleScreen tarzı Header */}
      <AppBar position="fixed" elevation={2} sx={{ zIndex: 1200, bgcolor: 'primary.main' }}>
        <Toolbar sx={{ justifyContent: 'space-between', alignItems: 'center', minHeight: 64, px: 2 }}>
          {/* Sol: Geri (admin) + Başlık */}
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
            {isAdmin && !isSmall && (
              <IconButton
                color="inherit"
                onClick={() => navigate('/restaurant-management')}
                sx={{ bgcolor: 'rgba(255, 255, 255, 0.1)', '&:hover': { bgcolor: 'rgba(255, 255, 255, 0.2)' } }}
              >
                <ArrowBackIcon />
              </IconButton>
            )}
            {isSmall ? (
              <Box sx={{ display: 'flex', alignItems: 'baseline', gap: 0.75 }}>
                <Typography variant="h6" component="h1" noWrap sx={{ fontWeight: 600 }}>
                  {setupDetails?.name || t('restaurantScreen.restaurant')}
                </Typography>
                {/* ✅ Sürüm numarası (package.json'dan otomatik) */}
                <Typography component="span" sx={{ fontSize: '0.7rem', opacity: 0.7, fontWeight: 500, letterSpacing: 0.3 }}>
                  v{APP_VERSION}
                </Typography>
              </Box>
            ) : (
              <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                <RestaurantMenuIcon sx={{ fontSize: 28 }} />
                <Box sx={{ display: 'flex', alignItems: 'baseline', gap: 0.75 }}>
                  <Typography variant="h6" component="h1" noWrap sx={{ fontWeight: 600 }}>
                    {t('restaurantScreen.title')}
                  </Typography>
                  {/* ✅ Sürüm numarası (package.json'dan otomatik) */}
                  <Typography component="span" sx={{ fontSize: '0.7rem', opacity: 0.7, fontWeight: 500, letterSpacing: 0.3 }}>
                    v{APP_VERSION}
                  </Typography>
                </Box>
                {setupDetails && (
                  <>
                    <Divider orientation="vertical" flexItem sx={{ mx: 1, bgcolor: 'rgba(255, 255, 255, 0.3)' }} />
                    <Typography variant="body1" sx={{ opacity: 0.9 }}>
                      {setupDetails.name}
                    </Typography>
                  </>
                )}
              </Box>
            )}
          </Box>

          {/* Orta Kısım: Garson/Kasiyer Bilgisi - desktop
              Sağdaki butonlarla aynı görsel ağırlık (kullanıcı isteği): height 40, fontSize 0.95rem, borderRadius 1.5. */}
          {!isSmall && (
            <Box sx={{ position: 'absolute', left: '50%', transform: 'translateX(-50%)', display: 'flex', alignItems: 'center', gap: 1.5 }}>
              {restaurantLoginUser && (
                <Chip 
                  label={`${restaurantLoginUser.loginType === 'cashier' ? t('restaurantScreen.cashier') : t('restaurantScreen.waiter')}: ${restaurantLoginUser.name}`} 
                  color={restaurantLoginUser.loginType === 'cashier' ? 'warning' : 'success'} 
                  variant="filled" 
                  sx={{ 
                    bgcolor: restaurantLoginUser.loginType === 'cashier' ? 'warning.main' : 'success.main', 
                    color: 'white', 
                    fontWeight: 700, 
                    fontSize: '0.95rem', 
                    height: 40,
                    borderRadius: 1.5,
                    '& .MuiChip-label': { px: 2 },
                  }} 
                />
              )}
            </Box>
          )}

          {/* Sağ: Desktop ikonları veya Mobile overflow */}
          {isSmall ? (
            <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
              {pavoTrackingPayments.length > 0 && (
                <Tooltip title={t('restaurantScreen.tooltips.pavoOrderTracking')}>
                  <IconButton color="inherit" onClick={() => setPavoTrackingOpen(true)} sx={{ bgcolor: 'rgba(206,147,216,0.3)', '&:hover': { bgcolor: 'rgba(206,147,216,0.5)' } }}>
                    <PointOfSaleIcon />
                  </IconButton>
                </Tooltip>
              )}
              <GuideButton
                guideKey="sales.restaurantScreen"
                color="inherit"
                tabIndex={-1}
                sx={{ bgcolor: 'rgba(255, 255, 255, 0.1)', '&:hover': { bgcolor: 'rgba(255, 255, 255, 0.2)' } }}
              />
              <Tooltip title={t('restaurantScreen.tooltips.groups')}>
                <IconButton color="inherit" onClick={toggleLeftDrawer(true)} sx={{ bgcolor: 'rgba(255, 255, 255, 0.1)', '&:hover': { bgcolor: 'rgba(255, 255, 255, 0.2)' } }}>
                  <FilterListIcon />
                </IconButton>
              </Tooltip>
              <Tooltip title={t('restaurantScreen.tooltips.menu')}>
                <IconButton color="inherit" onClick={openMobileMenu} sx={{ bgcolor: 'rgba(255, 255, 255, 0.1)', '&:hover': { bgcolor: 'rgba(255, 255, 255, 0.2)' } }}>
                  <MoreVertIcon />
                </IconButton>
              </Tooltip>
            </Box>
          ) : (
            <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
              {pavoTrackingPayments.length > 0 && (
                <Tooltip title={t('restaurantScreen.tooltips.pavoOrderTracking')}>
                  <Button
                    color="inherit"
                    variant="outlined"
                    startIcon={<PointOfSaleIcon sx={{ fontSize: '1.4rem !important' }} />}
                    onClick={() => setPavoTrackingOpen(true)}
                    sx={{
                      borderColor: 'rgba(255,255,255,0.5)',
                      color: 'white',
                      fontWeight: 700,
                      fontSize: '0.9rem',
                      height: 40,
                      px: 1.5,
                      borderRadius: 1.5,
                      '&:hover': { borderColor: 'white', bgcolor: 'rgba(255,255,255,0.1)' },
                    }}
                  >
                    Pavo ({pavoTrackingPayments.length})
                  </Button>
                </Tooltip>
              )}
              {/* Rehber (i) — kilit/tam ekran ile aynı boyut; tabIndex=-1: klavye odağını almaz. */}
              <GuideButton
                guideKey="sales.restaurantScreen"
                color="inherit"
                size="large"
                tabIndex={-1}
                sx={{
                  bgcolor: 'rgba(255, 255, 255, 0.1)',
                  p: 1.25,
                  '&:hover': { bgcolor: 'rgba(255, 255, 255, 0.2)' },
                  '& .MuiSvgIcon-root': { fontSize: '1.6rem' },
                }}
              />
              {/* Kilit & Tam Ekran — büyütüldü (kullanıcı isteği). */}
              <Tooltip title={t('restaurantScreen.tooltips.lockScreen')}>
                <IconButton
                  color="inherit"
                  onClick={handleLockScreen}
                  size="large"
                  sx={{
                    bgcolor: 'rgba(255, 255, 255, 0.1)',
                    p: 1.25,
                    '&:hover': { bgcolor: 'rgba(255, 255, 255, 0.2)' },
                  }}
                >
                  <LockIcon sx={{ fontSize: '1.6rem' }} />
                </IconButton>
              </Tooltip>
              <Tooltip title={isFullscreen ? t('restaurantScreen.tooltips.exitFullscreen') : t('restaurantScreen.tooltips.fullscreen')}>
                <IconButton
                  color="inherit"
                  onClick={toggleFullscreen}
                  size="large"
                  sx={{
                    bgcolor: 'rgba(255, 255, 255, 0.1)',
                    p: 1.25,
                    '&:hover': { bgcolor: 'rgba(255, 255, 255, 0.2)' },
                  }}
                >
                  {isFullscreen
                    ? <FullscreenExitIcon sx={{ fontSize: '1.85rem' }} />
                    : <FullscreenIcon sx={{ fontSize: '1.85rem' }} />}
                </IconButton>
              </Tooltip>
              {/* ✅ Profil ikonu (Avatar+PersonIcon) yerine "Menü" butonu (kullanıcı isteği).
                  Mevcut Menu/anchor mantığı (anchorElUser, handleOpenUserMenu, handleCloseUserMenu) korunuyor. */}
              <Tooltip title={userDisplayName || t('restaurantScreen.tooltips.menu')}>
                <Button
                  onClick={handleOpenUserMenu}
                  startIcon={<MenuIcon sx={{ fontSize: '1.5rem !important' }} />}
                  sx={{
                    bgcolor: 'white',
                    color: 'primary.dark',
                    px: 2,
                    py: 0.75,
                    fontWeight: 700,
                    textTransform: 'none',
                    fontSize: '0.95rem',
                    borderRadius: 1.5,
                    minHeight: 40,
                    '&:hover': {
                      bgcolor: 'primary.dark',
                      color: 'white',
                    },
                  }}
                >
                  {t('restaurantScreen.tooltips.menu')}
                </Button>
              </Tooltip>
            </Box>
          )}
        </Toolbar>
      </AppBar>

      {/* Kullanıcı Menüsü */}
      <Menu
        anchorEl={anchorElUser}
        open={Boolean(anchorElUser)}
        onClose={handleCloseUserMenu}
        anchorOrigin={{ vertical: 'bottom', horizontal: 'right' }}
        transformOrigin={{ vertical: 'top', horizontal: 'right' }}
        PaperProps={{
          elevation: 3,
          sx: { mt: 1, minWidth: 200 }
        }}
      >
        <MenuItem disabled>
          <Box>
            <Typography variant="body2" fontWeight="bold">
              {userDisplayName}
            </Typography>
            <Typography variant="caption" color="text.secondary">
              {authUser?.email}
            </Typography>
          </Box>
        </MenuItem>
        <Divider sx={{ my: 0.5 }} />
        <MenuItem onClick={() => { handleCloseUserMenu(); setIsLayoutPinDialogOpen(true); }}>
          <GridViewIcon fontSize="small" sx={{ mr: 1 }} />
          <Typography variant="body2">{t('restaurantScreen.menu.layoutSettings')}</Typography>
        </MenuItem>
        {restaurantLoginUser?.loginType === 'cashier' && restaurantLoginUser?.canViewEndOfDay && (
          <MenuItem onClick={() => { handleCloseUserMenu(); setEndOfDayPrintModalOpen(true); }}>
            <PrintIcon fontSize="small" sx={{ mr: 1 }} />
            <Typography variant="body2">{t('restaurantScreen.menu.endOfDay', 'Gün Sonu')}</Typography>
          </MenuItem>
        )}
        <Divider sx={{ my: 0.5 }} />
        <MenuItem onClick={handleLogout}>
          <LogoutIcon fontSize="small" sx={{ mr: 1 }} />
          <Typography variant="body2">{t('restaurantScreen.menu.logout')}</Typography>
        </MenuItem>
      </Menu>
      
      {/* Mobile Overflow Menu */}
      <Menu
        anchorEl={mobileMenuAnchor}
        open={Boolean(mobileMenuAnchor)}
        onClose={closeMobileMenu}
        anchorOrigin={{ vertical: 'bottom', horizontal: 'right' }}
        transformOrigin={{ vertical: 'top', horizontal: 'right' }}
      >
        {isAdmin && (
          <MenuItem onClick={() => { closeMobileMenu(); navigate('/restaurant-management'); }}>
            <ArrowBackIcon fontSize="small" style={{ marginRight: 8 }} />
            <Typography variant="body2">{t('restaurantScreen.menu.backToRestaurantScreens')}</Typography>
          </MenuItem>
        )}
        <MenuItem onClick={() => { closeMobileMenu(); handleLockScreen(); }}>
          <LockIcon fontSize="small" style={{ marginRight: 8 }} />
          <Typography variant="body2">{t('restaurantScreen.menu.lockScreen')}</Typography>
        </MenuItem>
        <MenuItem onClick={() => { closeMobileMenu(); toggleFullscreen(); }}>
          {isFullscreen ? <FullscreenExitIcon fontSize="small" style={{ marginRight: 8 }} /> : <FullscreenIcon fontSize="small" style={{ marginRight: 8 }} />}
          <Typography variant="body2">{isFullscreen ? t('restaurantScreen.menu.exitFullscreen') : t('restaurantScreen.menu.fullscreen')}</Typography>
        </MenuItem>
        {restaurantLoginUser?.loginType === 'cashier' && restaurantLoginUser?.canViewEndOfDay && (
          <MenuItem onClick={() => { closeMobileMenu(); setEndOfDayPrintModalOpen(true); }}>
            <PrintIcon fontSize="small" style={{ marginRight: 8 }} />
            <Typography variant="body2">{t('restaurantScreen.menu.endOfDay', 'Gün Sonu')}</Typography>
          </MenuItem>
        )}
        {restaurantLoginUser && (
          <MenuItem disabled>
            <Chip 
              label={`${restaurantLoginUser.loginType === 'cashier' ? t('restaurantScreen.cashier') : t('restaurantScreen.waiter')}: ${restaurantLoginUser.name}`} 
              color={restaurantLoginUser.loginType === 'cashier' ? 'warning' : 'success'} 
              size="small" 
              sx={{ 
                bgcolor: restaurantLoginUser.loginType === 'cashier' ? 'warning.main' : 'success.main', 
                color: 'white' 
              }} 
            />
          </MenuItem>
        )}
        <Divider />
        <MenuItem onClick={() => { closeMobileMenu(); handleOpenUserMenu({ currentTarget: document.body } as any); }}>
          <PersonIcon fontSize="small" style={{ marginRight: 8 }} />
          <Typography variant="body2">{t('restaurantScreen.menu.userMenu')}</Typography>
        </MenuItem>
      </Menu>

      <Toolbar sx={{ minHeight: 64 }} />
      <Box sx={{ display: 'flex', flexDirection: 'row', minHeight: 'calc(100vh - 64px)', height: 'calc(100vh - 64px)', overflow: 'hidden' }}>
        {/* Sol Menü - Masa grupları: Drawer (xs/sm) + Sidebar (md+) */}
        {/* Sidebar (md and up) */}
        <Box
          sx={{
            display: { xs: 'none', sm: 'none', md: 'flex' },
            width: { md: '240px' },
            minWidth: { md: '240px' },
            maxWidth: { md: '240px' },
            bgcolor: '#f5f7fa',
            borderRight: '1.5px solid #cfd8dc',
            height: '100%',
            flexDirection: 'column',
            alignItems: 'stretch',
            p: 1.5,
            zIndex: 10,
            position: 'relative',
          }}
        >
          <ToggleButtonGroup
            orientation="vertical"
            value={selectedGroupIdFilter}
            exclusive
            onChange={handleFilterChange}
            disabled={isLayoutEditMode}
            aria-label={t('restaurantScreen.aria.groupFilter')}
            sx={{
              width: '100%',
              height: 'auto',
              flexDirection: 'column',
              flexGrow: 1,
              border: 'none',
              gap: 1,
              opacity: isLayoutEditMode ? 0.5 : 1,
              '& .MuiToggleButton-root': {
                minHeight: { xs: 48, sm: 52, md: 56 },
                fontSize: { xs: '0.9rem', sm: '0.95rem', md: '1rem' },
                border: '1px solid #d0d7de !important',
                borderRadius: '8px !important',
                mb: 0.5,
                boxShadow: '0 1px 3px rgba(0,0,0,0.1)',
                '&:hover': {
                  boxShadow: '0 2px 6px rgba(0,0,0,0.15)',
                },
              },
            }}
          >
            <ToggleButton
              value="all"
              aria-label={t('restaurantScreen.aria.allTables')}
              sx={{
                flex: 'none',
                backgroundColor: selectedGroupIdFilter === 'all' ? 'primary.main' : 'white',
                color: selectedGroupIdFilter === 'all' ? 'white' : 'text.primary',
                '&:hover': {
                  backgroundColor: selectedGroupIdFilter === 'all' ? 'primary.dark' : '#e3f2fd',
                },
                '&.Mui-selected': {
                  backgroundColor: 'primary.main',
                  color: 'white',
                  '&:hover': {
                    backgroundColor: 'primary.dark',
                  },
                },
                textTransform: 'none',
                width: '100%',
                justifyContent: 'flex-start',
                pl: 2,
                fontWeight: selectedGroupIdFilter === 'all' ? 'bold' : 'normal',
              }}
            >
              <AllInclusiveIcon sx={{ mr: 1, fontSize: '1.2rem' }} />
              {t('restaurantScreen.all')}
            </ToggleButton>
            {tableGroups.map((group) => (
              <ToggleButton
                key={group.id}
                value={group.id}
                aria-label={group.name}
                sx={{
                  flex: 'none',
                  backgroundColor: selectedGroupIdFilter === group.id ? 'primary.main' : 'white',
                  color: selectedGroupIdFilter === group.id ? 'white' : 'text.primary',
                  '&:hover': {
                    backgroundColor: selectedGroupIdFilter === group.id ? 'primary.dark' : '#e3f2fd',
                  },
                  '&.Mui-selected': {
                    backgroundColor: 'primary.main',
                    color: 'white',
                    '&:hover': {
                      backgroundColor: 'primary.dark',
                    },
                  },
                  textTransform: 'none',
                  width: '100%',
                  justifyContent: 'flex-start',
                  pl: 2,
                  fontWeight: selectedGroupIdFilter === group.id ? 'bold' : 'normal',
                }}
              >
                {group.name}
              </ToggleButton>
            ))}
          </ToggleButtonGroup>
          
          {/* ✅ YENİ: Logo en altta */}
          <Box sx={{ mt: 2, display: 'flex', justifyContent: 'center', alignItems: 'center', py: 2 }}>
            <img 
              src={BRAND.logos.dark} 
              alt={`${BRAND.name} Logo`} 
              style={{ 
                width: '80%', 
                maxWidth: '180px',
                height: 'auto',
                opacity: 0.7
              }} 
            />
          </Box>
        </Box>

        {/* Drawer (xs/sm) */}
        <Drawer anchor="left" open={leftDrawerOpen} onClose={toggleLeftDrawer(false)}>
          <Box sx={{ width: 260, p: 1.5, display: 'flex', flexDirection: 'column', height: '100%' }} role="presentation" onKeyDown={toggleLeftDrawer(false)}>
            <Typography sx={{ fontWeight: 600, mb: 1 }}>{t('restaurantScreen.groupDrawer.title')}</Typography>
            <ToggleButtonGroup
              orientation="vertical"
              value={selectedGroupIdFilter}
              exclusive
              onChange={(e, v) => { handleFilterChange(e as any, v); toggleLeftDrawer(false)(); }}
              disabled={isLayoutEditMode}
              aria-label={t('restaurantScreen.aria.groupFilter')}
              sx={{
                width: '100%',
                flexDirection: 'column',
                gap: 1,
                flexGrow: 1,
                opacity: isLayoutEditMode ? 0.5 : 1,
                '& .MuiToggleButton-root': {
                  minHeight: 48,
                  fontSize: '0.95rem',
                  border: '1px solid #d0d7de !important',
                  borderRadius: '8px !important',
                },
              }}
            >
              <ToggleButton value="all" aria-label={t('restaurantScreen.aria.allTables')} sx={{ justifyContent: 'flex-start', pl: 2 }}>
                <AllInclusiveIcon sx={{ mr: 1, fontSize: '1.2rem' }} />
                {t('restaurantScreen.all')}
              </ToggleButton>
              {tableGroups.map((group) => (
                <ToggleButton key={group.id} value={group.id} aria-label={group.name} sx={{ justifyContent: 'flex-start', pl: 2 }}>
                  {group.name}
                </ToggleButton>
              ))}
            </ToggleButtonGroup>
            
            {/* ✅ YENİ: Logo en altta */}
            <Box sx={{ mt: 2, display: 'flex', justifyContent: 'center', alignItems: 'center', py: 2 }}>
              <img 
                src={BRAND.logos.dark} 
                alt={`${BRAND.name} Logo`} 
                style={{ 
                  width: '70%', 
                  maxWidth: '160px',
                  height: 'auto',
                  opacity: 0.7
                }} 
              />
            </Box>
          </Box>
        </Drawer>

        {/* Sağda içerik */}
        <Box sx={{ flexGrow: 1, minWidth: 0, p: { xs: 1, sm: 3 }, height: '100%', overflowY: 'auto' }}>
          {/* Düzenleme modu toolbar */}
          {isLayoutEditMode && (
            <Paper sx={{ p: 1.5, mb: 2, display: 'flex', alignItems: 'center', gap: 1.5, bgcolor: '#fff3e0', border: '2px solid #ff9800' }}>
              <GridViewIcon sx={{ color: '#e65100' }} />
              <Typography variant="body2" fontWeight="bold" sx={{ color: '#e65100', flex: 1 }}>
                {t('restaurantScreen.layoutEditModeDescription', { group: editingGroupId === 'all' ? t('restaurantScreen.all') : (tableGroups.find(g => g.id === editingGroupId)?.name || editingGroupId) })}
              </Typography>
              <Button variant="contained" color="info" size="small" startIcon={<PrintIcon />} onClick={() => setIsLocalPrinterModalOpen(true)}>
                {t('restaurantScreen.layoutToolbar.printerSettings')}
              </Button>
              <Button
                variant="contained"
                size="small"
                startIcon={<TextFieldsIcon />}
                onClick={() => setIsFontSizeModalOpen(true)}
                disabled={layoutSaving}
                sx={{ bgcolor: '#6a1b9a', '&:hover': { bgcolor: '#4a148c' } }}
              >
                {t('restaurantScreen.layoutToolbar.fontSize')}
              </Button>
              {/* ✅ YENİ: Masa açık süre sayacı toggle (sadece rakam, ikon yok) */}
              <FormControlLabel
                control={
                  <Switch
                    size="small"
                    checked={showTableOpenDuration}
                    onChange={(e) => setShowTableOpenDuration(e.target.checked)}
                    disabled={layoutSaving}
                  />
                }
                label="Masa Süresi (dk)"
                sx={{
                  ml: 0,
                  mr: 0,
                  '& .MuiFormControlLabel-label': {
                    fontSize: '0.8rem',
                    color: '#e65100',
                    fontWeight: 600,
                  },
                }}
              />
              {/* ✅ YENİ: Adisyon fişine masayı ilk açan garsonun adı yazılsın mı? (lokal) */}
              <FormControlLabel
                control={
                  <Switch
                    size="small"
                    checked={useTableOpenerForReceipt}
                    onChange={(e) => setUseTableOpenerForReceiptState(e.target.checked)}
                    disabled={layoutSaving}
                  />
                }
                label="Adisyonda İlk Açan Garson"
                sx={{
                  ml: 0,
                  mr: 0,
                  '& .MuiFormControlLabel-label': {
                    fontSize: '0.8rem',
                    color: '#e65100',
                    fontWeight: 600,
                  },
                }}
              />
              <Button variant="contained" color="success" size="small" startIcon={<SaveIcon />} onClick={handleSaveLayout} disabled={layoutSaving}>
                {layoutSaving ? t('restaurantScreen.layoutToolbar.saving') : t('restaurantScreen.layoutToolbar.save')}
              </Button>
              <Button variant="outlined" color="warning" size="small" startIcon={<RestartAltIcon />} onClick={handleResetLayout} disabled={layoutSaving}>
                {t('restaurantScreen.layoutToolbar.reset')}
              </Button>
              <Button variant="outlined" size="small" startIcon={<CloseIcon />} onClick={exitEditMode} disabled={layoutSaving}>
                {t('restaurantScreen.layoutToolbar.cancel')}
              </Button>
            </Paper>
          )}

          {error && (!loading && setupDetails) && (
            <Alert severity="warning" sx={{ mb: 2 }}>{error}</Alert>
          )}
          {Object.keys(filteredGroupedTables).length === 0 && !loading && (
            <Paper sx={{ p: 3, textAlign: 'center', mt: 2 }}>
              <Typography variant="subtitle1">
                {selectedGroupIdFilter === 'all'
                  ? t('restaurantScreen.empty.noTablesOrGroups')
                  : (tableGroups.find(g => g.id === selectedGroupIdFilter)
                    ? t('restaurantScreen.empty.noTablesInGroup', { groupName: tableGroups.find(g => g.id === selectedGroupIdFilter)?.name })
                    : t('restaurantScreen.empty.noTablesInSelectedGroup'))}
              </Typography>
              <Typography variant="body2" color="text.secondary" sx={{ mt: 1 }}>
                {selectedGroupIdFilter === 'all'
                  ? t('restaurantScreen.empty.manageHint')
                  : t('restaurantScreen.empty.selectDifferentGroupHint')}
              </Typography>
            </Paper>
          )}

          {/* Layout modu (düzenleme veya kaydedilmiş layout) */}
          {(() => {
            const currentGroupId = selectedGroupIdFilter;
            const groupSavedLayout = savedLayouts[currentGroupId] || [];
            const hasCustomLayout = groupSavedLayout.length > 0;
            const useGridLayout = isLayoutEditMode || hasCustomLayout;

            const currentGroupTables = getTablesForGroup(currentGroupId);

            if (useGridLayout && currentGroupTables.length > 0) {
              const activeLayout = isLayoutEditMode
                ? editingLayout
                : groupSavedLayout;

              const layoutMap = new Map(activeLayout.map(l => [l.i, l]));
              const finalLayout = currentGroupTables.map((table, index) => {
                const existing = layoutMap.get(table.id.toString());
                const base = existing || { i: table.id.toString(), x: (index % CARDS_PER_ROW) * DEFAULT_CARD_W, y: Math.floor(index / CARDS_PER_ROW) * DEFAULT_CARD_H, w: DEFAULT_CARD_W, h: DEFAULT_CARD_H };
                return { ...base, minW: MIN_CARD_W, minH: MIN_CARD_H };
              });

              return (
                <AutoWidthGridLayout
                  className="layout"
                  layout={finalLayout}
                  cols={LAYOUT_COLS}
                  rowHeight={LAYOUT_ROW_HEIGHT}
                  isDraggable={isLayoutEditMode}
                  isResizable={isLayoutEditMode}
                  onLayoutChange={isLayoutEditMode ? handleLayoutChange : undefined}
                  compactType={null}
                  preventCollision={true}
                  margin={[5, 5]}
                  containerPadding={[0, 0]}
                >
                  {currentGroupTables.map((table) => (
                    <div key={table.id.toString()}>
                      <Card
                        sx={{
                          backgroundColor: getTableCardBackgroundColor(table),
                          height: '100%',
                          border: isLayoutEditMode ? '2px dashed rgba(255,255,255,0.4)' : '1px solid',
                          borderColor: isLayoutEditMode ? undefined : 'divider',
                          cursor: isLayoutEditMode ? 'move' : 'pointer',
                          overflow: 'hidden',
                        }}
                        elevation={isLayoutEditMode ? 3 : 0}
                      >
                        <CardActionArea
                          onClick={() => !isLayoutEditMode && handleTableClick(table)}
                          disabled={isLayoutEditMode}
                          sx={{
                            height: '100%',
                            p: '2px',
                            display: 'flex',
                            flexDirection: 'column',
                            justifyContent: 'center',
                            alignItems: 'center',
                            overflow: 'hidden',
                            minHeight: 0,
                          }}
                        >
                          <Typography
                            fontWeight="bold"
                            color="white"
                            noWrap
                            title={table.name}
                            sx={{
                              fontSize: `${2 * tableNameFontScale}rem`,
                              lineHeight: 1.2,
                              flexShrink: 0,
                            }}
                          >
                            {table.name.replace('Masa ', '')}
                          </Typography>
                          {pavoPaymentsByTable[table.name] ? (
                            <Typography sx={{ fontSize: '0.5rem', fontWeight: 'bold', color: 'white', bgcolor: 'rgba(255,255,255,0.25)', borderRadius: 0.5, px: 0.5, lineHeight: 1.4 }}>PAVO</Typography>
                          ) : (
                            <TableRestaurantIcon
                              sx={{
                                fontSize: 18,
                                color: 'white',
                                flexShrink: 1,
                                minHeight: 0,
                                overflow: 'hidden',
                              }}
                            />
                          )}
                          {renderTableOpenDurationLabel(table)}
                          <Chip
                            icon={<ChairIcon sx={{ fontSize: '0.55rem', color: 'white !important' }} />}
                            label={table.capacity > 0 ? `${table.capacity}` : '-'}
                            size="small"
                            variant="outlined"
                            sx={{
                              display: 'none',
                              fontSize: '0.45rem',
                              height: 14,
                              color: 'white',
                              borderColor: 'white',
                              flexShrink: 1,
                              minHeight: 0,
                              overflow: 'hidden',
                            }}
                          />
                        </CardActionArea>
                      </Card>
                    </div>
                  ))}
                </AutoWidthGridLayout>
              );
            }

            // Varsayılan MUI Grid görünümü
            if (selectedGroupIdFilter === 'all') {
              return (
                <Grid container spacing={1.25}>
                  {sortedAllTables.map((table) => (
                    <Grid item xs={4} sm={3} md={2} lg={1.5} xl={1.2} key={table.id}>
                      <Card sx={{ backgroundColor: getTableCardBackgroundColor(table), height: '100%', border: '1px solid', borderColor: 'divider' }} elevation={0}>
                        <CardActionArea onClick={() => handleTableClick(table)} sx={{ width: '90%', mx: 'auto', minHeight: { xs: 100, sm: 105, md: 110 }, maxHeight: { xs: 120, sm: 125, md: 130 }, aspectRatio: '1 / 1', p: { xs: 0.6, sm: 0.6, md: 0.5 }, display: 'flex', flexDirection: 'column', justifyContent: 'center', alignItems: 'center' }}>
                          <Box sx={{ display: 'flex', flexDirection: 'column', alignItems: 'center', width: '100%', mb: 0.2 }}>
                            <Typography variant="h4" component="div" sx={{ textAlign: 'center', width: '100%', lineHeight: 1.15, fontSize: { xs: `${1.6 * tableNameFontScale}rem`, sm: `${1.8 * tableNameFontScale}rem`, md: `${2 * tableNameFontScale}rem`, lg: `${2.2 * tableNameFontScale}rem` }, fontWeight: 'bold', color: 'white', mb: 0.3, overflow: 'hidden', textOverflow: 'ellipsis', display: '-webkit-box', WebkitLineClamp: 2, WebkitBoxOrient: 'vertical', wordBreak: 'break-word' }} title={table.name}>
                              {table.name.replace('Masa ', '')}
                            </Typography>
                            {pavoPaymentsByTable[table.name] ? (
                              <Typography sx={{ fontSize: '0.7rem', fontWeight: 'bold', color: 'white', bgcolor: 'rgba(255,255,255,0.25)', borderRadius: 1, px: 0.8, py: 0.1 }}>PAVO</Typography>
                            ) : (
                              <TableRestaurantIcon sx={{ fontSize: { xs: 32, sm: 34, md: 36, lg: 38 }, mt: 0.2, color: 'white' }} />
                            )}
                            {renderTableOpenDurationLabel(table)}
                          </Box>
                          <Box sx={{ display: 'none', width: '100%', textAlign: 'center', mt: 'auto' }}>
                            <Chip icon={<ChairIcon sx={{ fontSize: '0.75rem', color: 'white !important' }} />} label={table.capacity > 0 ? `${table.capacity}` : '-'} size="small" variant="outlined" sx={{ fontSize: '0.55rem', p: '0 4px', height: 18, mb: 0.2, color: 'white', borderColor: 'white' }} />
                          </Box>
                        </CardActionArea>
                      </Card>
                    </Grid>
                  ))}
                </Grid>
              );
            }

            return groupOrder.map(groupId => {
              const groupInfo = filteredGroupedTables[groupId];
              if (!groupInfo || groupInfo.tables.length === 0) return null;
              return (
                <Grid container spacing={1.25} key={groupId}>
                  {groupInfo.tables.map((table) => (
                    <Grid item xs={4} sm={3} md={2} lg={1.5} xl={1.2} key={table.id}>
                      <Card sx={{ backgroundColor: getTableCardBackgroundColor(table), height: '100%', border: '1px solid', borderColor: 'divider' }} elevation={0}>
                        <CardActionArea onClick={() => handleTableClick(table)} sx={{ width: '90%', mx: 'auto', minHeight: { xs: 100, sm: 105, md: 110 }, maxHeight: { xs: 120, sm: 125, md: 130 }, aspectRatio: '1 / 1', p: { xs: 0.6, sm: 0.6, md: 0.5 }, display: 'flex', flexDirection: 'column', justifyContent: 'center', alignItems: 'center' }}>
                          <Box sx={{ display: 'flex', flexDirection: 'column', alignItems: 'center', width: '100%', mb: 0.2 }}>
                            <Typography variant="h4" component="div" sx={{ textAlign: 'center', width: '100%', lineHeight: 1.15, fontSize: { xs: `${1.6 * tableNameFontScale}rem`, sm: `${1.8 * tableNameFontScale}rem`, md: `${2 * tableNameFontScale}rem`, lg: `${2.2 * tableNameFontScale}rem` }, fontWeight: 'bold', color: 'white', mb: 0.3, overflow: 'hidden', textOverflow: 'ellipsis', display: '-webkit-box', WebkitLineClamp: 2, WebkitBoxOrient: 'vertical', wordBreak: 'break-word' }} title={table.name}>
                              {table.name.replace('Masa ', '')}
                            </Typography>
                            {pavoPaymentsByTable[table.name] ? (
                              <Typography sx={{ fontSize: '0.7rem', fontWeight: 'bold', color: 'white', bgcolor: 'rgba(255,255,255,0.25)', borderRadius: 1, px: 0.8, py: 0.1 }}>PAVO</Typography>
                            ) : (
                              <TableRestaurantIcon sx={{ fontSize: { xs: 32, sm: 34, md: 36, lg: 38 }, mt: 0.2, color: 'white' }} />
                            )}
                            {renderTableOpenDurationLabel(table)}
                          </Box>
                          <Box sx={{ display: 'none', width: '100%', textAlign: 'center', mt: 'auto' }}>
                            <Chip icon={<ChairIcon sx={{ fontSize: '0.75rem', color: 'white !important' }} />} label={table.capacity > 0 ? `${table.capacity}` : '-'} size="small" variant="outlined" sx={{ fontSize: '0.55rem', p: '0 4px', height: 18, mb: 0.2, color: 'white', borderColor: 'white' }} />
                          </Box>
                        </CardActionArea>
                      </Card>
                    </Grid>
                  ))}
                </Grid>
              );
            });
          })()}
        </Box>
      </Box>

      {/* Mobil Menü - Drawer */}
      <Drawer
        anchor="left"
        open={drawerOpen}
        onClose={toggleDrawer(false)}
        PaperProps={{
          sx: {
            width: 250,
            bgcolor: '#f5f7fa',
            borderRight: '1px solid #cfd8dc',
          },
        }}
      >
        <Box sx={{ p: 2, display: 'flex', flexDirection: 'column', height: '100%' }}>
          {/* Menü Başlığı */}
          <Typography variant="h6" component="div" sx={{ fontWeight: 600, mb: 2 }}>
            {t('restaurantScreen.title')}
          </Typography>

          {/* Masa Grupları Listesi */}
          <List sx={{ flexGrow: 1 }}>
            <ListItem disablePadding>
              <ListItemButton
                selected={selectedGroupIdFilter === 'all'}
                onClick={() => { setSelectedGroupIdFilter('all'); if (setupIdParam) sessionStorage.setItem(`restaurant_group_${setupIdParam}`, 'all'); }}
                sx={{ borderRadius: 2 }}
              >
                <AllInclusiveIcon sx={{ mr: 1 }} />
                <ListItemText primary={t('restaurantScreen.all')} primaryTypographyProps={{ fontWeight: selectedGroupIdFilter === 'all' ? 'medium' : 'regular' }} />
              </ListItemButton>
            </ListItem>
            {tableGroups.map((group) => (
              <ListItem key={group.id} disablePadding>
                <ListItemButton
                  selected={selectedGroupIdFilter === group.id}
                  onClick={() => { setSelectedGroupIdFilter(group.id); if (setupIdParam) sessionStorage.setItem(`restaurant_group_${setupIdParam}`, group.id); }}
                  sx={{ borderRadius: 2 }}
                >
                  <ListItemText primary={group.name} primaryTypographyProps={{ fontWeight: selectedGroupIdFilter === group.id ? 'medium' : 'regular' }} />
                </ListItemButton>
              </ListItem>
            ))}
          </List>

          {/* Menü Altı Butonları */}
          <Box sx={{ mt: 2 }}>
            <Button
              variant="contained"
              color="primary"
              fullWidth
              onClick={toggleDrawer(false)}
              sx={{ borderRadius: 2, py: 1.5, fontWeight: 'medium' }}
            >
              {t('restaurantScreen.addTable')}
            </Button>
          </Box>
        </Box>
      </Drawer>

      {/* PIN Giriş Dialog */}
      {isLayoutPinDialogOpen && (
        <Box sx={{ position: 'fixed', top: 0, left: 0, right: 0, bottom: 0, bgcolor: 'rgba(0,0,0,0.7)', zIndex: 9999, display: 'flex', alignItems: 'center', justifyContent: 'center' }}
          onClick={() => { setIsLayoutPinDialogOpen(false); setLayoutPinInput(''); setLayoutPinError(false); }}
        >
          <Paper sx={{ p: 3, borderRadius: 3, minWidth: 320, maxWidth: 360 }} onClick={(e) => e.stopPropagation()}>
            <Typography variant="h6" textAlign="center" gutterBottom fontWeight="bold">
              {t('restaurantScreen.adminPassword.title')}
            </Typography>
            <Typography variant="body2" textAlign="center" color="text.secondary" sx={{ mb: 2 }}>
              {t('restaurantScreen.adminPassword.description')}
            </Typography>

            {/* PIN gösterge */}
            <Box sx={{ display: 'flex', justifyContent: 'center', gap: 1.5, mb: 3 }}>
              {[0, 1, 2, 3, 4].map((i) => (
                <Box key={i} sx={{
                  width: 40, height: 40, borderRadius: '50%',
                  border: '2px solid',
                  borderColor: layoutPinError ? 'error.main' : (i < layoutPinInput.length ? 'primary.main' : 'grey.300'),
                  bgcolor: i < layoutPinInput.length ? (layoutPinError ? 'error.main' : 'primary.main') : 'transparent',
                  transition: 'all 0.2s',
                  display: 'flex', alignItems: 'center', justifyContent: 'center',
                }}>
                  {i < layoutPinInput.length && <Box sx={{ width: 12, height: 12, borderRadius: '50%', bgcolor: 'white' }} />}
                </Box>
              ))}
            </Box>
            {layoutPinError && (
              <Typography variant="caption" color="error" textAlign="center" display="block" sx={{ mb: 1 }}>
                {t('restaurantScreen.adminPassword.wrongPassword')}
              </Typography>
            )}

            {/* Numpad */}
            <Box sx={{ display: 'grid', gridTemplateColumns: 'repeat(3, 1fr)', gap: 1 }}>
              {['1', '2', '3', '4', '5', '6', '7', '8', '9', '', '0', 'del'].map((key) => {
                if (key === '') return <Box key="empty" />;
                if (key === 'del') {
                  return (
                    <Button key="del" variant="outlined" onClick={handlePinBackspace} sx={{ height: 56, fontSize: '1.2rem' }}>
                      <BackspaceIcon />
                    </Button>
                  );
                }
                return (
                  <Button key={key} variant="outlined" onClick={() => handlePinDigit(key)} sx={{ height: 56, fontSize: '1.4rem', fontWeight: 'bold' }}>
                    {key}
                  </Button>
                );
              })}
            </Box>

            <Button fullWidth variant="text" onClick={() => { setIsLayoutPinDialogOpen(false); setLayoutPinInput(''); setLayoutPinError(false); }} sx={{ mt: 2 }}>
              {t('restaurantScreen.layoutToolbar.cancel')}
            </Button>
          </Paper>
        </Box>
      )}

      {/* Snackbar - Bildirimler */}
      <Snackbar
        open={snackbarOpen}
        autoHideDuration={3000}
        onClose={handleSnackbarClose}
        message={snackbarMessage}
        anchorOrigin={{ vertical: 'bottom', horizontal: 'center' }}
      />

      {/* Local Yazıcı Ayarları Modalı */}
      <LocalPrinterSettingsModal
        open={isLocalPrinterModalOpen}
        onClose={() => setIsLocalPrinterModalOpen(false)}
        companyId={companyId}
        quickSaleScreenId={setupDetails?.quickSaleScreenId ?? null}
      />

      <PavoOrderTrackingModal
        open={pavoTrackingOpen}
        onClose={() => setPavoTrackingOpen(false)}
        payments={pavoTrackingPayments}
        onPaymentCancelled={loadActivePavoPayments}
      />

      {/* ✨ Pavo Restoran Ödendi Onay — KOYU MOR masaya tıklayınca açılan info modal */}
      <PavoPaymentClosedInfoModal
        open={pavoClosedInfoModal.open}
        sale={pavoClosedInfoModal.sale}
        tableName={pavoClosedInfoModal.tableName}
        companyId={companyId}
        quickSaleScreenId={setupDetails?.quickSaleScreenId ?? null}
        confirmedByName={restaurantLoginUser?.name ?? null}
        confirmedById={restaurantLoginUser?.id ?? null}
        onClose={() => setPavoClosedInfoModal({ open: false, sale: null })}
        onConfirmed={() => {
          // Listeyi anında yenile (10sn polling beklenmez)
          loadActiveSales();
        }}
      />

      {/* Masa İsmi Yazı Boyutu Modalı */}
      <TableNameFontSizeModal
        open={isFontSizeModalOpen}
        onClose={() => setIsFontSizeModalOpen(false)}
        currentScale={tableNameFontScale}
        onApply={(scale) => setTableNameFontScale(scale)}
      />
    </Box>
    </>
  );
};

export default RestaurantScreen;
